/**
 * In-browser implementation of Backend/DataSource: the permanent marketing
 * demo (/demo, /demo/mitglied, …). It is a product feature, not a prototype
 * left over: every new DataSource method needs its implementation here too.
 *
 * No network, ever. Nothing in this module's import graph reaches the API
 * client (`demoNoNetwork.test.ts` walks it), and a demo session never
 * receives the API backend (the clients' backend factories). Drawings come
 * from an injected loader — the web reads static files on its own origin,
 * the native app bundles them — and fall back to the built-in silhouettes.
 *
 * Behaves like the API is specified to: tenant scoping with one membership
 * check (no membership → 404, ADR 0004), role from the membership of the
 * requested tenant, members only touch their own unreviewed reports, status
 * transitions from core, soft delete, one change event per write, undo as a
 * compensating write. Persisted through an injected synchronous storage
 * (localStorage on the web; on the native app an in-memory copy that writes
 * through to AsyncStorage) so a reload keeps the state; every storage access
 * is wrapped because private windows and full quotas throw.
 */
import {
  ALL_VIEWS,
  BASE_VIEWS,
  inspectAvatar,
  inspectLogo,
  DEFAULT_MIRROR_RULES,
  PINS_DRIFT,
  placeholderSizeOf,
  RETENTION,
  SORT_ORDER_MAX,
  SORT_ORDER_STEP,
  TYPE_CREDIT_MAX,
  TYPE_CREDIT_MIN,
  TYPE_NAME_MAX,
  TYPE_NAME_MIN,
  de,
  drift,
  driftMessage,
  isAnonymizedLabel,
  isComplete,
  MAX_ADDITIONAL_EMAILS,
  LOGIN_CODE_TTL_MINUTES,
  isValidRegistration,
  isValidTypeCode,
  validateDrawing,
  withDrawing,
  DRAWING_MESSAGES,
  ANONYMIZED_PREFIX,
  type MirrorRules,
  actionNeedsReason,
  mayAddMedia,
  mediaRequirementMet,
  canTransition,
  findNearby,
  DAMAGE_NUMBER_MAX,
  formatDamageNumber,
  hasAtLeastRole,
  isMemberParticipation,
  type MemberWrite,
  participationAllows,
  NEARBY_VISIBLE_STATUSES,
  OPEN_STATUSES,
  REVIEW_NOTE_MIN_LENGTH,
  resolveView,
  targetStatus,
  validateDamageTitle,
  validateDescription,
  COMMENT_MAX,
  MAX_IMAGES_PER_DAMAGE,
  VIDEO_MAX_PER_DAMAGE,
  DAMAGE_STATUSES,
  normalizeEmail,
  normalizeCategories,
  isValidEmail,
  type AircraftClass,
  type AircraftView,
  type BaseView,
  type DamageStatus,
  isAircraftClass,
  activeNotices,
  berlinDay,
  isAcknowledgementCurrent,
  isNoticeActive,
  isNoticeDay,
  isNoticeLevel,
  mayManageNotices,
  noticeLevelAllowed,
  NOTICE_BODY_MAX,
  NOTICE_TITLE_MAX,
  NOTICE_TITLE_MIN,
  type NoticeLevel,
} from '@fsv/core';
import { placeholderDrawings } from '../drawings/placeholder';
import { tx } from '../i18n/extra';
import { ageInDays } from '../lib/format';
import { searchDamages } from '../lib/search';
import { buildZip, dataUrl, utf8 } from '../lib/zip';
import { firstError, optional, validateContact } from '../lib/tenantValidation';
import { demoAnnouncements } from '../news/news';
import { DataError, type Backend, type DataSource, type PlatformSource } from './DataSource';
import { createSeed, DEFAULT_SETTINGS, exportStamp } from './demoSeed';
import { randomId } from '../lib/ids';
import { base64ToBytes } from '../lib/base64';
import {
  DEMO_STATE_VERSION,
  type DemoState,
  type StoredAircraft,
  type StoredDamage,
  type StoredDrawingUpload,
  type StoredExport,
  type StoredType,
  type StoredEvent,
  type StoredImage,
  type StoredNotice,
  type StoredPerson,
  type StoredTenant,
} from './demoState';
import { placeholderPhoto } from './placeholderPhoto';
import { parseDemoPhotoRef, webDemoPhotoUrl, type DemoPhotoUrl } from './demoPhotos';
import type {
  Aircraft,
  AircraftNotice,
  AircraftPatch,
  AircraftType,
  ChangeEvent,
  ClubContact,
  Damage,
  DamageComment,
  DamageImage,
  Drawing,
  DrawingUploadResult,
  ExportLink,
  ExportResult,
  FleetAircraft,
  FleetAircraftType,
  FleetEntry,
  FleetView,
  ForceOptions,
  JsonObject,
  Me,
  Member,
  MyEmails,
  PersonRef,
  Settings,
  Tenant,
  TenantContact,
  TenantRole,
  TrashItem,
  WriteOptions,
} from './types';
import { TENANT_CONTACT_FIELDS } from './types';

export const DEMO_STORAGE_KEY = 'fsv.demo.state.v6';
const STORAGE_KEY = DEMO_STORAGE_KEY;
/** Keys of earlier versions (tenant-less, then the FSV-based seed); removed so they do not linger in the quota. */
/** Keys of earlier demo versions; removed on start, so an async platform preloads them too. */
export const DEMO_LEGACY_KEYS = ['fsv.demo.state', 'fsv.demo.state.v4'] as const;
const OLD_STORAGE_KEYS = DEMO_LEGACY_KEYS;
/**
 * Which "Neuigkeiten" each demo persona has dismissed, beside the state rather
 * than in it: it is not club data, and keeping it out of DemoState leaves the
 * stored shape (and DEMO_STATE_VERSION) alone. "Demo zurücksetzen" clears it.
 */
export const DEMO_NEWS_KEY = 'fsv.demo.news.v1';

/**
 * Every key the demo store reads or writes. A platform whose storage is
 * asynchronous (the app) preloads exactly these before the demo starts; a key
 * missing here is written but never read back after a restart — the news
 * cards came back on every launch that way. `demoPersistedKeys.test.ts`
 * holds this list and the store's storage calls together.
 */
export const DEMO_PERSISTED_KEYS = [DEMO_STORAGE_KEY, DEMO_NEWS_KEY] as const;

/** The part of the Web Storage API the demo store uses. Synchronous on purpose: every write is one call. */
export interface DemoStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface DemoSourceOptions {
  /** Who is acting. Null means signed out; every call then fails like a 401 would. */
  getActor: () => Me | null;
  now?: () => Date;
  /** Where the state survives a restart; null keeps it in memory for this session only. */
  storage?: DemoStorage | null;
  /** Artificial latency so loading states are visible; 0 in tests. */
  latencyMs?: number;
  /** The three base drawings of a type, from wherever the platform keeps them. Default: the built-in silhouettes. */
  loadDrawings?: (typeCode: string) => Promise<Record<BaseView, Drawing>>;
  /** The storage refused the full state (quota); the large photo copies were dropped. */
  onStorageFull?: () => void;
  /**
   * A copy of an uploaded drawing small enough for the storage (the web: a
   * canvas scaled to ≤ 800 px, PNG, transparency kept). Width and height of
   * the ORIGINAL stay what pins are measured against. Without it, or when it
   * fails, the original bytes are kept as a data URL.
   */
  prepareDrawing?: (blob: Blob) => Promise<string | null>;
  /**
   * File name of a real demo photo (demoPhotos.ts) → URL. Default: the web's
   * public path; the app passes its bundled assets.
   */
  photoUrl?: DemoPhotoUrl;
}

/** A stable, unique stand-in for the API's sha256 label (the demo needs no crypto): 16 hex digits of two FNV-1a passes. */
export function demoAnonymizedLabel(memberId: string): string {
  const fnv = (seed: number) => {
    let h = seed >>> 0;
    for (const ch of memberId.toLowerCase()) {
      h ^= ch.charCodeAt(0);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h.toString(16).padStart(8, '0');
  };
  return ANONYMIZED_PREFIX + fnv(0x811c9dc5) + fnv(0x01000193);
}

const newId = randomId;

/**
 * The demo sends no mail: confirming a further address (0023) takes this code,
 * and the profile says so in the demo. Never accepted anywhere but here.
 */
export const DEMO_EMAIL_CODE = '123456';

/** A stored image ref → what an <img> loads: `photo:` via the platform's resolver, `placeholder:` drawn here, anything else as is. */
export function resolveImageRef(ref: string, photoUrl: DemoPhotoUrl = webDemoPhotoUrl): string {
  const real = parseDemoPhotoRef(ref);
  if (real) return photoUrl(real.thumb ? real.photo.thumb : real.photo.file);
  if (!ref.startsWith('placeholder:')) return ref;
  const [, category = 'sonstiges', seed = '1', overview = '0'] = ref.split(':');
  return placeholderPhoto(category as Parameters<typeof placeholderPhoto>[0], Number(seed), overview === '1');
}

function damageSnapshot(d: StoredDamage, numberOf: (id: string | null) => number | null): JsonObject {
  return {
    title: d.title,
    description: d.description,
    category: d.categories[0],
    categories: [...d.categories],
    status: d.status,
    view: d.view,
    x: d.x,
    y: d.y,
    reviewNote: d.reviewNote,
    mergedIntoId: d.mergedIntoId,
    mergedIntoNumber: numberOf(d.mergedIntoId),
  };
}

/** The acting person inside one tenant, resolved per call like requireMembership() in the API. */
interface Actor {
  id: string;
  role: TenantRole;
  tenant: StoredTenant;
}

export type DemoBackend = Backend & { readonly state: DemoState };

/**
 * A demo video is a blob: URL that dies with the page; read back from storage it
 * would be a broken tile. They go, the rest of the state stays (stage 2).
 */
export function dropSessionVideos(state: DemoState): DemoState {
  return { ...state, images: state.images.filter((i) => i.kind !== 'video') };
}

export function createDemoBackend(options: DemoSourceOptions): DemoBackend {
  const storage = options.storage ?? null;
  const loadDrawingsForType = options.loadDrawings ?? ((code: string) => Promise.resolve(placeholderDrawings(code)));
  const now = options.now ?? (() => new Date());
  const latency = options.latencyMs ?? 60;
  const photoUrl = options.photoUrl ?? webDemoPhotoUrl;
  const resolveUrl = (ref: string) => resolveImageRef(ref, photoUrl);
  let state = load();

  function load(): DemoState {
    try {
      for (const k of OLD_STORAGE_KEYS) storage?.removeItem(k);
      const raw = storage?.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as DemoState;
        if (parsed.version === DEMO_STATE_VERSION) return dropSessionVideos(parsed);
      }
    } catch {
      /* corrupt or blocked: start fresh */
    }
    return createSeed(now());
  }

  function save(): void {
    if (!storage) return;
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Quota: full-size photos are the bulk. Keep thumbnails, drop the large copies.
      try {
        // Uploaded drawings no type uses any more (replaced ones) go too; their undo then fails like a lost blob.
        const used = new Set(state.types.flatMap((t) => Object.values(t.drawings).map((d) => d?.uploadId)));
        const slim: DemoState = {
          ...state,
          images: state.images.map((i) => (i.url.startsWith('data:') ? { ...i, url: i.thumbUrl } : i)),
          drawingUploads: state.drawingUploads.map((u) => (used.has(u.id) ? u : { ...u, src: '' })),
        };
        storage.setItem(STORAGE_KEY, JSON.stringify(slim));
      } catch {
        /* still too big: the session keeps working in memory */
      }
      options.onStorageFull?.();
    }
  }

  const wait = () => (latency > 0 ? new Promise<void>((r) => setTimeout(r, latency)) : Promise.resolve());
  const iso = () => now().toISOString();
  const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;
  const notFound = () => new DataError(404, tx.errors.notAllowed);

  // The notice routes' checks (packages/api/src/notices/input.ts), same texts.
  function noticeLevel(value: unknown): NoticeLevel {
    if (!isNoticeLevel(value)) throw new DataError(400, de.notices.invalidLevel);
    return value;
  }
  function noticeTitle(value: unknown): string {
    const t = typeof value === 'string' ? value.trim() : '';
    if (t.length < NOTICE_TITLE_MIN || t.length > NOTICE_TITLE_MAX) throw new DataError(400, de.fleet.textLength('Der Hinweis', NOTICE_TITLE_MIN, NOTICE_TITLE_MAX));
    return t;
  }
  function noticeBody(value: unknown): string | null {
    if (value === null || value === undefined) return null;
    const t = typeof value === 'string' ? value.trim() : '';
    if (t.length > NOTICE_BODY_MAX) throw new DataError(400, de.fleet.textLength('Der Text', 0, NOTICE_BODY_MAX));
    return t.length === 0 ? null : t;
  }
  function noticeDay(value: unknown, today: string): string | null {
    if (value === null || value === undefined || value === '') return null;
    if (!isNoticeDay(value)) throw new DataError(400, de.notices.invalidDay);
    if (value < today) throw new DataError(400, 'Der letzte Tag liegt in der Vergangenheit.');
    return value;
  }

  // ------------------------------------------------------------------ people
  /**
   * The signed-in demo persona. Anyone else (a real session that somehow got
   * here) is refused: real people never read or write demo data.
   */
  function currentPerson(): StoredPerson {
    const me = options.getActor();
    if (!me) throw new DataError(403, tx.errors.notAllowed);
    const p = state.people.find((x) => x.id === me.memberId);
    if (!p) throw new DataError(403, tx.errors.notAllowed);
    return p;
  }

  function personRef(id: string | null): PersonRef | null {
    if (!id) return null;
    const p = state.people.find((x) => x.id === id);
    return { id, name: p?.displayName ?? 'Unbekannt', avatarUrl: p?.avatar ?? null };
  }

  const tenantById = (tenantId: string): StoredTenant | undefined => state.tenants.find((t) => t.id === tenantId);

  /**
   * The single access decision for tenant data (requireMembership in the API):
   * unknown tenant, inactive tenant, no or inactive membership → 404.
   */
  function requireMembership(tenantId: string): Actor {
    const p = currentPerson();
    const t = tenantById(tenantId);
    if (!t || !t.isActive) throw notFound();
    const m = state.memberships.find((x) => x.tenantId === t.id && x.memberId === p.id && x.isActive);
    if (!m) throw notFound();
    return { id: p.id, role: m.role, tenant: t };
  }

  // ------------------------------------------------------------------ tenants
  function toTenant(t: StoredTenant): Tenant {
    return {
      id: t.id,
      name: t.name,
      slug: t.slug,
      code: t.code,
      clubName: t.clubName,
      street: t.street,
      postalCode: t.postalCode,
      city: t.city,
      country: t.country,
      phone: t.phone,
      contactEmail: t.contactEmail,
      website: t.website,
      notes: t.notes,
      isActive: t.isActive,
      createdAt: t.createdAt,
      counts: {
        members: state.memberships.filter((m) => m.tenantId === t.id && m.isActive).length,
        aircraft: state.aircraft.filter((a) => a.tenantId === t.id && !a.deletedAt).length,
        damages: state.damages.filter((d) => d.tenantId === t.id && !d.deletedAt).length,
      },
    };
  }

  function tenantEvent(t: StoredTenant, actorId: string, action: 'created' | 'updated' | 'image_added' | 'image_removed', before: JsonObject | null, after: JsonObject | null, opts?: WriteOptions): number {
    const id = state.nextEventId++;
    state.events.push({ id, tenantId: t.id, actorId, entityType: 'tenant', entityId: t.id, entityLabel: t.name, damageId: null, action: opts?.undoOf ? 'undo' : action, before, after, reason: null, undoOf: opts?.undoOf ?? null, createdAt: iso() });
    return id;
  }

  /** Applies contact fields from a patch; returns before/after of what changed. */
  function applyContact(t: StoredTenant, patch: Partial<TenantContact>): { before: JsonObject; after: JsonObject } {
    const error = firstError(validateContact(patch));
    if (error) throw new DataError(400, error);
    const before: JsonObject = {};
    const after: JsonObject = {};
    for (const k of TENANT_CONTACT_FIELDS) {
      if (patch[k] === undefined) continue;
      const v = optional(patch[k]);
      if (v === t[k]) continue;
      before[k] = t[k];
      after[k] = v;
      t[k] = v;
    }
    return { before, after };
  }

  /** Person by e-mail, created when new (members is cross-tenant). */
  /** First or live further address (0023): an address belongs to one person, as in the API. */
  function personByAnyAddress(normalized: string): StoredPerson | undefined {
    return state.people.find(
      (x) => normalizeEmail(x.email) === normalized || (x.additionalEmails ?? []).some((e) => e.email === normalized),
    );
  }

  /**
   * As the API's personToJoin: across clubs only a proven address (the first,
   * or one the person confirmed: `self`) identifies a person. A club-added
   * further address of somebody outside club `tenantId` is `'unproven'` and
   * refused, never attached (security review 28.10.2026).
   */
  function personToJoin(normalized: string, tenantId: string): StoredPerson | 'unproven' | undefined {
    const p = personByAnyAddress(normalized);
    if (!p || normalizeEmail(p.email) === normalized) return p;
    const via = (p.additionalEmails ?? []).find((e) => e.email === normalized);
    if (via?.source === 'self') return p;
    return state.memberships.some((m) => m.memberId === p.id && m.tenantId === tenantId) ? p : 'unproven';
  }

  function addressesOf(p: StoredPerson): string[] {
    return [normalizeEmail(p.email), ...(p.additionalEmails ?? []).map((e) => e.email)];
  }

  function myEmailsOf(p: StoredPerson): MyEmails {
    const pending = p.pendingEmail && Date.parse(p.pendingEmail.expiresAt) > now().getTime() ? p.pendingEmail : null;
    return {
      primary: p.email,
      additional: (p.additionalEmails ?? []).map((e) => ({ ...e })),
      pending: pending ? { email: pending.email, expiresAt: pending.expiresAt } : null,
    };
  }

  /** The persona's own change, recorded in every club they are active in — as the API does. */
  function ownAddressEvent(p: StoredPerson, change: 'addressAdded' | 'addressRemoved'): void {
    for (const m of state.memberships.filter((x) => x.memberId === p.id && x.isActive)) {
      state.events.push({
        id: state.nextEventId++,
        tenantId: m.tenantId,
        actorId: p.id,
        entityType: 'member',
        entityId: p.id,
        entityLabel: p.displayName,
        damageId: null,
        action: 'updated',
        before: { [change]: false },
        after: { [change]: true },
        reason: null,
        undoOf: null,
        createdAt: iso(),
      });
    }
  }

  function personFor(email: string, name: string): StoredPerson {
    const normalized = normalizeEmail(email);
    let p = personByAnyAddress(normalized);
    if (!p) {
      p = { id: newId(), email: normalized, displayName: name.trim(), lastSignInAt: null, createdAt: iso(), providers: [] };
      state.people.push(p);
    }
    return p;
  }

  // ------------------------------------------------------------------ drawings
  const uploadById = (id: string) => state.drawingUploads.find((u) => u.id === id);

  /** What an image view shows for an upload: its stored copy, or a drawing of the free demo set. */
  async function uploadUrl(u: StoredDrawingUpload | undefined): Promise<string> {
    if (!u || !u.src) return '';
    if (!u.src.startsWith('demo:')) return u.src;
    const [, code = '', view = 'oben'] = u.src.split(':');
    const set = await loadDrawingsForType(code);
    return set[view as BaseView]?.url ?? '';
  }

  /**
   * The drawings a type is shown with, like the API's toAircraftTypeDto:
   * catalogue from the loader; an own type its uploads once all three base
   * views exist (an own derived view as an extra key), the silhouettes before.
   */
  async function drawingsOfType(t: StoredType): Promise<AircraftType['drawings']> {
    if (t.tenantId === null) return loadDrawingsForType(t.code);
    if (!isComplete(t.drawings)) return placeholderDrawings(t.code);
    const out: Partial<Record<AircraftView, Drawing>> = {};
    for (const view of ALL_VIEWS) {
      const d = t.drawings[view];
      if (d) out[view] = { url: await uploadUrl(uploadById(d.uploadId)), width: d.width, height: d.height, placeholder: false };
    }
    return out as AircraftType['drawings'];
  }

  /**
   * What drift() needs about a type (packages/api drawingSize): the size each
   * image view is measured against. Catalogue: the loader's drawing; own: the
   * upload once complete, else the API's placeholder (1500 × 1000, the cabin 1000 × 1500).
   */
  async function geometryOf(t: StoredType, drawings = t.drawings, rules: MirrorRules = t.mirrorRules) {
    const catalogue = t.tenantId === null ? await loadDrawingsForType(t.code) : null;
    const complete = isComplete(drawings);
    return {
      rules,
      sizeOf: (view: AircraftView) => {
        if (catalogue) {
          const d = catalogue[view as BaseView];
          return d && !d.placeholder ? { width: d.width, height: d.height } : placeholderSizeOf(view);
        }
        const own = drawings[view];
        return own && complete ? { width: own.width, height: own.height } : placeholderSizeOf(view);
      },
    };
  }

  // ------------------------------------------------------------------ export
  /** In-memory archives of this session, by name, as data URLs; rebuilt from the current state when missing (after a reload). */
  const archives = new Map<string, { url: string; size: number }>();

  /**
   * The club's data as a club admin's export lays it out (tables/<name>.json
   * with schemaVersion, manifest.json), from the demo state: without the
   * platform's own note on the club. Photos are not included: the demo's are
   * generated pictures, and the manifest says so.
   */
  function buildArchive(t: StoredTenant, at: Date, by: string): { bytes: Uint8Array; tables: Record<string, number> } {
    const memberIds = new Set(state.memberships.filter((m) => m.tenantId === t.id).map((m) => m.memberId));
    const aircraft = state.aircraft.filter((a) => a.tenantId === t.id);
    const typeIds = new Set(aircraft.map((a) => a.typeId));
    const damages = state.damages.filter((d) => d.tenantId === t.id);
    const damageIds = new Set(damages.map((d) => d.id));
    const tables: Record<string, unknown[]> = {
      tenants: [{ ...t, notes: null }],
      members: state.people.filter((p) => memberIds.has(p.id)).map((p) => ({ id: p.id, email: p.email, displayName: p.displayName, lastSignInAt: p.lastSignInAt, createdAt: p.createdAt })),
      memberships: state.memberships.filter((m) => m.tenantId === t.id),
      settings: [{ tenantId: t.id, ...(state.settings[t.id] ?? DEFAULT_SETTINGS) }],
      aircraft_types: state.types.filter((x) => x.tenantId === t.id || (x.tenantId === null && typeIds.has(x.id))).map((x) => ({ ...x, drawings: Object.fromEntries(Object.entries(x.drawings).map(([v, d]) => [v, { width: d?.width, height: d?.height }])) })),
      aircraft,
      damages,
      damage_images: state.images.filter((i) => damageIds.has(i.damageId)).map((i) => ({ id: i.id, damageId: i.damageId, width: i.width, height: i.height, isOverview: i.isOverview, uploadedBy: i.uploadedBy, createdAt: i.createdAt, deletedAt: i.deletedAt, deletedBy: i.deletedBy })),
      damage_comments: state.comments.filter((c) => damageIds.has(c.damageId)),
      change_events: state.events.filter((e) => e.tenantId === t.id),
    };
    const counts = Object.fromEntries(Object.entries(tables).map(([k, rows]) => [k, rows.length]));
    const manifest = {
      schemaVersion: 1,
      tenant: { id: t.id, name: t.name, slug: t.slug, code: t.code },
      exportedAt: at.toISOString(),
      exportedBy: by,
      tables: counts,
      photos: { count: 0, missing: [] },
      notes: [tx.export.demoManifestNote],
    };
    const entries = [
      { name: 'manifest.json', data: utf8(JSON.stringify(manifest, null, 2)) },
      ...Object.entries(tables).map(([table, rows]) => ({ name: `tables/${table}.json`, data: utf8(JSON.stringify({ schemaVersion: 1, table, rows })) })),
    ];
    return { bytes: buildZip(entries, at), tables: counts };
  }

  function exportLink(e: StoredExport): ExportLink {
    let archive = archives.get(e.name);
    if (!archive) {
      const t = tenantById(e.tenantId);
      if (!t) return { name: e.name, size: e.size, createdAt: e.createdAt, url: '', expiresAt: e.createdAt };
      const bytes = buildArchive(t, new Date(e.createdAt), e.createdBy).bytes;
      archive = { url: dataUrl('application/zip', bytes), size: bytes.length };
      archives.set(e.name, archive);
    }
    // Like the API's read SAS: a fresh link per response, one hour.
    return { name: e.name, size: e.size || archive.size, createdAt: e.createdAt, url: archive.url, expiresAt: new Date(now().getTime() + 3_600_000).toISOString() };
  }

  function createExportFor(t: StoredTenant, actorId: string): { value: ExportResult; eventId: number } {
    const at = now();
    const { bytes, tables } = buildArchive(t, at, actorId);
    const name = `${t.id}/${exportStamp(at.toISOString())}.zip`;
    archives.set(name, { url: dataUrl('application/zip', bytes), size: bytes.length });
    const record: StoredExport = { tenantId: t.id, name, size: bytes.length, createdAt: at.toISOString(), createdBy: actorId };
    state.exports.push(record);
    const eventId = state.nextEventId++;
    state.events.push({ id: eventId, tenantId: t.id, actorId, entityType: 'tenant', entityId: t.id, entityLabel: t.name, damageId: null, action: 'exported', before: null, after: { file: name, size: bytes.length, tables, photos: 0 }, reason: null, undoOf: null, createdAt: at.toISOString() });
    save();
    return { value: { ...exportLink(record), tables, photos: { count: 0, missing: [] } }, eventId };
  }

  function listExportsOf(tenantId: string): ExportLink[] {
    const cutoff = now().getTime() - RETENTION.exportDays * 86_400_000;
    return state.exports
      .filter((e) => e.tenantId === tenantId && Date.parse(e.createdAt) >= cutoff)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .map(exportLink);
  }

  // ------------------------------------------------------------------ platform
  /**
   * The demo has no platform manager (demoPersonas.ts says why), so every
   * /api/platform/* call answers like the API answers anyone else: 404
   * (rule 17). The interface needs the methods; the demo needs nothing more.
   */
  const refuse = async (): Promise<never> => {
    await wait();
    currentPerson();
    throw notFound();
  };
  const platform: PlatformSource = {
    listTenants: refuse,
    getTenant: refuse,
    createTenant: refuse,
    updateTenant: refuse,
    addTenantAdmin: refuse,
    createTenantExport: refuse,
    listTenantExports: refuse,
    listContracts: refuse,
    getContract: refuse,
    createContract: refuse,
    updateContract: refuse,
    terminateContract: refuse,
    addContractDepartment: refuse,
    removeContractDepartment: refuse,
    fleetCounts: refuse,
    billing: refuse,
    billingCsv: refuse,
  };

  // ------------------------------------------------------------------ tenant source
  const sources = new Map<string, DataSource>();

  function createTenantSource(tenantId: string): DataSource {
    const actor = () => requireMembership(tenantId);

    function requireRole(min: TenantRole): Actor {
      const a = actor();
      if (!hasAtLeastRole(a.role, min)) throw new DataError(403, tx.errors.notAllowed);
      return a;
    }

    const numberOf = (id: string | null) => (id ? (state.damages.find((d) => d.id === id)?.number ?? null) : null);
    /** The reference as this club writes it (FSV-000042); '' for a damage that is gone. */
    const refOf = (a: Actor, n: number | null) => (n === null ? '' : formatDamageNumber(n, a.tenant.code));

    /** dbo.next_damage_number: the club's counter, one up, refused past Z99999. */
    function nextDamageNumber(a: Actor): number {
      const last = state.damageCounters[a.tenant.id] ?? 0;
      if (last >= DAMAGE_NUMBER_MAX) throw new DataError(409, tx.errors.numbersExhausted);
      state.damageCounters[a.tenant.id] = last + 1;
      return last + 1;
    }
    /** A live (not deleted) aircraft of this tenant; deleted ones exist only for the fleet admin. */
    const aircraftOf = (a: Actor, id: string) => state.aircraft.find((x) => x.id === id && x.tenantId === a.tenant.id && !x.deletedAt);
    const liveAircraftOf = (a: Actor) => state.aircraft.filter((x) => x.tenantId === a.tenant.id && !x.deletedAt);
    const typeOf = (id: string) => state.types.find((t) => t.id === id);
    const settingsOf = (a: Actor): Settings => ({ ...(state.settings[a.tenant.id] ?? DEFAULT_SETTINGS) });
    /** The club's member_participation, asked where the API asks it (after the 404s), same 403 text. */
    const requireParticipation = (a: Actor, write: MemberWrite) => {
      if (participationAllows(a.role, settingsOf(a).memberParticipation, write)) return;
      throw new DataError(403, write === 'report' ? de.participation.reportOff : de.participation.commentOff);
    };

    function toAircraft(a: DemoState['aircraft'][number]): Aircraft {
      const t = typeOf(a.typeId);
      return { id: a.id, registration: a.registration, typeId: a.typeId, isActive: a.isActive, sortOrder: a.sortOrder, typeCode: t?.code ?? '?', typeName: t?.name ?? '?' };
    }

    function liveImages(damageId: string): StoredImage[] {
      return state.images.filter((i) => i.damageId === damageId && !i.deletedAt);
    }

    function toDamage(d: StoredDamage): Damage {
      const a = state.aircraft.find((x) => x.id === d.aircraftId);
      const t = a ? typeOf(a.typeId) : undefined;
      const imgs = liveImages(d.id);
      const thumb = imgs.find((i) => !i.isOverview) ?? imgs[0];
      return {
        id: d.id,
        number: d.number,
        aircraftId: d.aircraftId,
        registration: a?.registration ?? '?',
        typeCode: t?.code ?? '?',
        category: d.categories[0]!,
        categories: [...d.categories],
        view: d.view,
        x: d.x,
        y: d.y,
        title: d.title,
        description: d.description,
        status: d.status,
        reportedBy: personRef(d.reportedBy) ?? { id: d.reportedBy, name: '?' },
        reportedAt: d.reportedAt,
        updatedAt: d.updatedAt,
        resolvedAt: d.resolvedAt,
        reviewedBy: personRef(d.reviewedBy),
        reviewedAt: d.reviewedAt,
        reviewNote: d.reviewNote,
        mergedIntoId: d.mergedIntoId,
        mergedIntoNumber: numberOf(d.mergedIntoId),
        imageCount: imgs.length,
        thumbUrl: thumb ? resolveUrl(thumb.thumbUrl) : null,
        deletedAt: d.deletedAt,
      };
    }

    function toImage(i: StoredImage): DamageImage {
      return {
        id: i.id,
        damageId: i.damageId,
        url: resolveUrl(i.url),
        thumbUrl: resolveUrl(i.thumbUrl),
        width: i.width,
        height: i.height,
        isOverview: i.isOverview,
        ...(i.kind === 'video' ? { kind: 'video' as const, durationMs: i.durationMs ?? null } : {}),
        uploadedBy: personRef(i.uploadedBy) ?? { id: i.uploadedBy, name: '?' },
        createdAt: i.createdAt,
      };
    }

    function toComment(c: DemoState['comments'][number]): DamageComment {
      return { id: c.id, damageId: c.damageId, author: personRef(c.authorId) ?? { id: c.authorId, name: '?' }, body: c.body, createdAt: c.createdAt };
    }

    function toEvent(e: StoredEvent): ChangeEvent {
      return { id: e.id, entityType: e.entityType, entityId: e.entityId, entityLabel: e.entityLabel, damageId: e.damageId, action: e.action, before: clone(e.before), after: clone(e.after), reason: e.reason, undoOf: e.undoOf, createdAt: e.createdAt, actor: personRef(e.actorId) };
    }

    function toMember(m: DemoState['memberships'][number]): Member | null {
      const p = state.people.find((x) => x.id === m.memberId);
      if (!p) return null;
      return { id: p.id, email: p.email, displayName: p.displayName, avatarUrl: p.avatar ?? null, role: m.role, isActive: m.isActive, lastSignInAt: p.lastSignInAt, createdAt: m.createdAt, providers: [...p.providers], invitation: m.invitation ?? null, additionalEmails: m.isActive ? (p.additionalEmails ?? []).map((e) => ({ ...e })) : [] };
    }

    const damagesOf = (a: Actor) => state.damages.filter((d) => d.tenantId === a.tenant.id);
    const membershipsOf = (a: Actor) => state.memberships.filter((m) => m.tenantId === a.tenant.id);

    /** Damage of this tenant: deleted ones only for admins. Foreign/deleted = 404, never 403. */
    function findDamage(a: Actor, id: string, includeDeleted = false): StoredDamage {
      const d = state.damages.find((x) => x.id === id && x.tenantId === a.tenant.id);
      if (!d || (d.deletedAt && !includeDeleted)) throw notFound();
      return d;
    }

    function findImage(a: Actor, imageId: string, live: boolean): StoredImage {
      const img = state.images.find((i) => i.id === imageId && (!live || !i.deletedAt));
      if (!img || !state.damages.some((d) => d.id === img.damageId && d.tenantId === a.tenant.id)) throw notFound();
      return img;
    }

    function findComment(a: Actor, commentId: string, live: boolean) {
      const c = state.comments.find((x) => x.id === commentId && (!live || !x.deletedAt));
      if (!c || !state.damages.some((d) => d.id === c.damageId && d.tenantId === a.tenant.id)) throw notFound();
      return c;
    }

    function findNotice(a: Actor, id: string): StoredNotice {
      const n = state.notices.find((x) => x.id === id && x.tenantId === a.tenant.id);
      if (!n || !aircraftOf(a, n.aircraftId)) throw notFound();
      return n;
    }

    /** The response shape; `readers` only for editors and admins, like the API. */
    function toNotice(a: Actor, n: StoredNotice): AircraftNotice {
      const mine = state.noticeAcks.find((k) => k.noticeId === n.id && k.memberId === a.id)?.acknowledgedAt ?? null;
      const readers = mayManageNotices(a.role)
        ? state.noticeAcks
            .filter((k) => k.noticeId === n.id && isAcknowledgementCurrent(k.acknowledgedAt, n.revisedAt))
            .sort((x, y) => y.acknowledgedAt.localeCompare(x.acknowledgedAt))
            .map((k) => ({ person: personRef(k.memberId) ?? { id: k.memberId, name: '?' }, acknowledgedAt: k.acknowledgedAt }))
        : undefined;
      return {
        id: n.id,
        aircraftId: n.aircraftId,
        registration: aircraftOf(a, n.aircraftId)?.registration ?? '',
        level: n.level,
        title: n.title,
        body: n.body,
        validUntil: n.validUntil,
        createdAt: n.createdAt,
        createdBy: personRef(n.createdBy),
        revisedAt: n.revisedAt,
        updatedAt: n.updatedAt,
        resolvedAt: n.resolvedAt,
        resolvedBy: personRef(n.resolvedBy),
        acknowledgedAt: isAcknowledgementCurrent(mine, n.revisedAt) ? mine : null,
        ...(readers ? { readers } : {}),
      };
    }

    function event(a: Actor, e: Omit<StoredEvent, 'id' | 'createdAt' | 'actorId' | 'undoOf' | 'tenantId'>, opts?: WriteOptions): number {
      const id = state.nextEventId++;
      state.events.push({ ...e, tenantId: a.tenant.id, action: opts?.undoOf ? 'undo' : e.action, id, actorId: a.id, undoOf: opts?.undoOf ?? null, createdAt: iso() });
      return id;
    }

    /** Undo is allowed for the person who did the original write, regardless of the role the inverse needs. */
    function isOwnUndo(a: Actor, opts?: WriteOptions): boolean {
      if (!opts?.undoOf) return false;
      const original = state.events.find((e) => e.id === opts.undoOf && e.tenantId === a.tenant.id);
      return original?.actorId === a.id;
    }

    function isOwnUnreviewed(d: StoredDamage, a: Actor): boolean {
      return d.reportedBy === a.id && d.status === 'gemeldet' && d.reviewedAt === null;
    }

    async function aspectOf(a: Actor, d: { aircraftId: string; view: AircraftView }): Promise<number> {
      const ac = aircraftOf(a, d.aircraftId);
      const t = ac ? typeOf(ac.typeId) : undefined;
      if (!t) return 1;
      const drawings = await drawingsOfType(t);
      // A mirrored view through its base; an own image of a derived view is its own base.
      const { base } = resolveView(d.view, t.mirrorRules);
      const drawing = drawings[base] ?? drawings.oben;
      return drawing.height / drawing.width;
    }

    async function nearbyOf(a: Actor, d: StoredDamage): Promise<StoredDamage[]> {
      const candidates = damagesOf(a).filter((x) => x.id !== d.id && !x.deletedAt && x.aircraftId === d.aircraftId && x.view === d.view && NEARBY_VISIBLE_STATUSES.includes(x.status));
      const hits = findNearby(d, candidates, settingsOf(a).duplicateRadius, await aspectOf(a, d));
      return hits.map((h) => h.item);
    }

    function addStoredImages(damageId: string, images: { dataUrl: string; thumbDataUrl: string; width: number; height: number; isOverview: boolean; kind?: 'photo' | 'video'; durationMs?: number }[], by: string): StoredImage[] {
      // Photos and videos count separately, like the API (0018).
      const live = liveImages(damageId);
      const isVideo = (x: { kind?: 'photo' | 'video' }) => x.kind === 'video';
      if (live.filter((x) => !isVideo(x)).length + images.filter((x) => !isVideo(x)).length > MAX_IMAGES_PER_DAMAGE) {
        throw new DataError(400, tx.create.tooManyPhotos(MAX_IMAGES_PER_DAMAGE));
      }
      if (live.filter(isVideo).length + images.filter(isVideo).length > VIDEO_MAX_PER_DAMAGE) {
        throw new DataError(400, tx.video.tooMany(VIDEO_MAX_PER_DAMAGE));
      }
      const created = images.map((img) => ({
        id: newId(),
        damageId,
        url: img.dataUrl,
        thumbUrl: img.thumbDataUrl,
        width: img.width,
        height: img.height,
        isOverview: img.kind === 'video' ? false : img.isOverview,
        ...(img.kind === 'video' ? { kind: 'video' as const, durationMs: img.durationMs ?? null } : {}),
        uploadedBy: by,
        createdAt: iso(),
        deletedAt: null,
        deletedBy: null,
      }));
      state.images.push(...created);
      return created;
    }


    // ---------------------------------------------------------------- fleet
    /** Catalogue and this club's own types (another club's own type does not exist for it). */
    const visibleTypes = (a: Actor) => state.types.filter((t) => t.tenantId === null || t.tenantId === a.tenant.id);

    async function toAircraftType(t: StoredType): Promise<AircraftType> {
      return { id: t.id, code: t.code, name: t.name, credit: t.credit, aircraftClass: t.aircraftClass ?? null, mirrorRules: { ...t.mirrorRules }, drawings: await drawingsOfType(t) };
    }

    /** The fleet editor's view of a type (packages/api fleet/present.ts, toFleetTypeDto). */
    async function toFleetType(a: Actor, t: StoredType, withCount = true): Promise<FleetAircraftType> {
      const catalogue = t.tenantId === null ? await loadDrawingsForType(t.code) : null;
      const views = {} as Record<AircraftView, FleetView>;
      const empty = { mirroredFrom: null, url: '', width: null, height: null, uploadId: null };
      for (const view of ALL_VIEWS) {
        const { base, mirrored } = resolveView(view, t.mirrorRules);
        if (mirrored) {
          views[view] = { ...empty, source: 'mirrored', mirroredFrom: base as BaseView };
        } else if (catalogue) {
          const d = catalogue[base as BaseView];
          views[view] = d && !d.placeholder ? { ...empty, source: 'bundled', url: d.url, width: d.width, height: d.height } : { ...empty, source: 'missing' };
        } else {
          const own = t.drawings[view];
          views[view] = own ? { ...empty, source: 'own', url: await uploadUrl(uploadById(own.uploadId)), width: own.width, height: own.height, uploadId: own.uploadId } : { ...empty, source: 'missing' };
        }
      }
      return {
        ...(await toAircraftType(t)),
        scope: t.tenantId === null ? 'catalogue' : 'tenant',
        complete: catalogue ? BASE_VIEWS.every((v) => views[v].source === 'bundled') : isComplete(t.drawings),
        aircraftCount: withCount ? liveAircraftOf(a).filter((x) => x.typeId === t.id).length : null,
        drawingsLocked: t.tenantId === null,
        views,
      };
    }

    /** All damages of an aircraft, also those in the trash (fleet rules count them all). */
    const damageCountOf = (ac: StoredAircraft) => state.damages.filter((d) => d.aircraftId === ac.id && d.tenantId === ac.tenantId).length;

    function toFleetAircraft(ac: StoredAircraft): FleetAircraft {
      const damageCount = damageCountOf(ac);
      return { ...toAircraft(ac), isDemo: false, deletedAt: ac.deletedAt, damageCount, registrationLocked: damageCount > 0, deletable: damageCount === 0 && ac.deletedAt === null };
    }

    const aircraftSnapshot = (ac: StoredAircraft): JsonObject => ({ registration: ac.registration, typeId: ac.typeId, typeCode: typeOf(ac.typeId)?.code ?? '?', sortOrder: ac.sortOrder, isActive: ac.isActive });

    /** Only the keys whose values differ, as the API records an 'updated' event. */
    function changed(before: JsonObject, after: JsonObject): { before: JsonObject; after: JsonObject } {
      const b: JsonObject = {};
      const n: JsonObject = {};
      for (const k of Object.keys(after)) {
        if (JSON.stringify(before[k]) !== JSON.stringify(after[k])) {
          b[k] = before[k];
          n[k] = after[k];
        }
      }
      return { before: b, after: n };
    }

    /** Undo (docs/api.md): only by the person who wrote the event, and only once. */
    function checkUndo(a: Actor, opts?: WriteOptions): void {
      if (!opts?.undoOf) return;
      const original = state.events.find((e) => e.id === opts.undoOf && e.tenantId === a.tenant.id);
      if (!original || state.events.some((e) => e.undoOf === opts.undoOf)) throw new DataError(409, tx.toast.undoFailed);
      if (original.actorId !== a.id) throw new DataError(403, tx.errors.notAllowed);
    }

    /** A type an aircraft may use: the catalogue or this club's own; anything else is the same 400. */
    function usableType(a: Actor, typeId: string): StoredType {
      const t = typeOf(typeId);
      if (!t || (t.tenantId !== null && t.tenantId !== a.tenant.id)) throw new DataError(400, de.fleet.unknownType);
      return t;
    }

    /** A type a write may change: only the club's own. Foreign: 404, catalogue: 403. */
    function ownType(a: Actor, typeId: string): StoredType {
      const t = typeOf(typeId);
      if (!t || (t.tenantId !== null && t.tenantId !== a.tenant.id)) throw notFound();
      if (t.tenantId === null) throw new DataError(403, de.fleet.catalogueType);
      return t;
    }

    function parseRegistration(value: string): string {
      if (!isValidRegistration(value)) throw new DataError(400, de.fleet.registrationFormat);
      return value.trim().toUpperCase();
    }

    function checkSortOrder(value: number): number {
      if (!Number.isInteger(value) || value < 0 || value > SORT_ORDER_MAX) throw new DataError(400, de.fleet.sortOrderRange(SORT_ORDER_MAX));
      return value;
    }

    function checkText(value: string, min: number, max: number, what: string): string {
      const t = value.trim();
      if (t.length < min || t.length > max) throw new DataError(400, de.fleet.textLength(what, min, max));
      return t;
    }

    /** The class of a type (0025), with the API's words: required, one of four, never back to unknown. */
    function checkAircraftClass(value: unknown): AircraftClass {
      if (value === undefined || value === null || value === '') throw new DataError(400, de.aircraftClass.required);
      if (!isAircraftClass(value)) throw new DataError(400, de.aircraftClass.unknown);
      return value;
    }

    /** The registration is unique per club, deleted aircraft included (restore instead). */
    function checkRegistrationFree(a: Actor, registration: string, exceptId: string | null): void {
      const holder = state.aircraft.find((x) => x.tenantId === a.tenant.id && x.registration === registration && x.id !== exceptId);
      if (holder) throw new DataError(409, holder.deletedAt ? de.fleet.registrationInTrash : de.fleet.registrationTaken);
    }

    function damagesPerView(a: Actor, of: { aircraftId: string } | { typeId: string }): Partial<Record<AircraftView, number>> {
      const ids = new Set('aircraftId' in of ? [of.aircraftId] : state.aircraft.filter((x) => x.tenantId === a.tenant.id && x.typeId === of.typeId).map((x) => x.id));
      const out: Partial<Record<AircraftView, number>> = {};
      for (const d of damagesOf(a)) if (ids.has(d.aircraftId)) out[d.view] = (out[d.view] ?? 0) + 1;
      return out;
    }

    /** checkDrift of the API: a change that moves pins needs force; with it, the reason the event records. */
    function checkDrift(drifts: ReturnType<typeof drift>, force: boolean, details?: Record<string, unknown>): string | null {
      if (drifts.length === 0) return null;
      const message = driftMessage(drifts);
      if (!force) throw new DataError(409, message, PINS_DRIFT, details);
      return message;
    }

    function findFleetAircraft(a: Actor, id: string): StoredAircraft {
      const ac = state.aircraft.find((x) => x.id === id && x.tenantId === a.tenant.id);
      if (!ac) throw notFound();
      return ac;
    }

    /** PUT …/drawings/{view} of the API: register an upload for a view, with the drift question. */
    async function registerDrawing(a: Actor, typeId: string, view: AircraftView, uploadId: string, opts?: ForceOptions): Promise<DrawingUploadResult> {
      const t = ownType(a, typeId);
      const upload = state.drawingUploads.find((u) => u.id === uploadId && u.typeId === t.id && u.view === view);
      if (!upload || !upload.src) throw new DataError(400, DRAWING_MESSAGES.missing, 'missing');
      checkUndo(a, opts);
      const entry = { uploadId: upload.id, width: upload.width, height: upload.height };
      const next = withDrawing(t.drawings, t.mirrorRules, view, entry);
      const before = await geometryOf(t);
      const after = await geometryOf(t, next.drawings, next.rules);
      const reason = checkDrift(drift(damagesPerView(a, { typeId: t.id }), before, after), opts?.force === true, { uploadId: upload.id });
      const previous = t.drawings[view];
      t.drawings = next.drawings;
      t.mirrorRules = next.rules;
      const eventId = event(
        a,
        {
          entityType: 'aircraft_type',
          entityId: t.id,
          entityLabel: t.code,
          damageId: null,
          action: 'updated',
          before: previous ? { view, blob: previous.uploadId, width: previous.width, height: previous.height } : { view, blob: null, mirroredFrom: before.rules[view] ?? null },
          after: { view, blob: upload.id, width: upload.width, height: upload.height },
          reason,
        },
        opts,
      );
      save();
      return { value: await toFleetType(a, t, false), eventId, warnings: upload.alpha ? [] : ['no_transparency'] };
    }

    const source: DataSource = {
      kind: 'demo',
      tenantId,

      async getSettings() {
        await wait();
        return settingsOf(actor());
      },

      async listAircraftTypes(): Promise<AircraftType[]> {
        await wait();
        const a = actor();
        // The catalogue and this club's own types; another club's own type does not exist here.
        return Promise.all(visibleTypes(a).map(toAircraftType));
      },

      async listFleet(): Promise<FleetEntry[]> {
        await wait();
        const a = actor();
        return liveAircraftOf(a)
          .filter((x) => x.isActive)
          .sort((p, q) => p.sortOrder - q.sortOrder)
          .map((ac) => {
            const ds = damagesOf(a).filter((d) => d.aircraftId === ac.id && !d.deletedAt);
            const counts = Object.fromEntries(DAMAGE_STATUSES.map((s) => [s, 0])) as Record<DamageStatus, number>;
            for (const d of ds) counts[d.status] += 1;
            const open = ds.filter((d) => OPEN_STATUSES.includes(d.status));
            const last = ds.reduce<string | null>((max, d) => (max === null || d.reportedAt > max ? d.reportedAt : max), null);
            return {
              aircraft: toAircraft(ac),
              counts,
              openCount: open.length,
              lastReportAt: last,
              openPins: open.map((d) => ({ id: d.id, number: d.number, view: d.view, x: d.x, y: d.y, status: d.status, category: d.categories[0]!, categories: [...d.categories] })),
            };
          });
      },

      async listAircraft() {
        await wait();
        const a = actor();
        return liveAircraftOf(a)
          .sort((p, q) => p.sortOrder - q.sortOrder)
          .map(toAircraft);
      },

      async listDamages(registration) {
        await wait();
        const a = actor();
        const ac = liveAircraftOf(a).find((x) => x.registration === registration.toUpperCase());
        if (!ac) throw new DataError(404, tx.plan.notFound);
        return damagesOf(a)
          .filter((d) => d.aircraftId === ac.id && !d.deletedAt)
          .sort((p, q) => q.reportedAt.localeCompare(p.reportedAt))
          .map(toDamage);
      },

      async getDamage(id) {
        await wait();
        const a = actor();
        const d = findDamage(a, id, a.role === 'admin');
        const ac = aircraftOf(a, d.aircraftId);
        if (!ac) throw notFound();
        return {
          damage: toDamage(d),
          aircraft: toAircraft(ac),
          images: liveImages(d.id).map(toImage),
          comments: state.comments.filter((c) => c.damageId === d.id && !c.deletedAt).sort((p, q) => p.createdAt.localeCompare(q.createdAt)).map(toComment),
          events: state.events.filter((e) => e.tenantId === a.tenant.id && e.damageId === d.id).sort((p, q) => q.id - p.id).map(toEvent),
          mergedFrom: damagesOf(a).filter((x) => x.mergedIntoId === d.id && !x.deletedAt).map((x) => ({ id: x.id, number: x.number })),
        };
      },

      async search(query) {
        await wait();
        const a = actor();
        const activeIds = new Set(liveAircraftOf(a).filter((x) => x.isActive).map((x) => x.id));
        const all = damagesOf(a).filter((d) => !d.deletedAt && activeIds.has(d.aircraftId)).map(toDamage);
        return searchDamages(all, query, now(), a.tenant.code);
      },

      async inbox() {
        await wait();
        const a = requireRole('editor');
        const due = settingsOf(a).reviewDueDays;
        const reports = damagesOf(a).filter((d) => d.status === 'gemeldet' && !d.deletedAt).sort((p, q) => p.reportedAt.localeCompare(q.reportedAt));
        return Promise.all(
          reports.map(async (d) => {
            const ageDays = ageInDays(d.reportedAt, now());
            return { damage: toDamage(d), nearby: (await nearbyOf(a, d)).map(toDamage), ageDays, overdue: ageDays > due };
          }),
        );
      },

      async myReports() {
        await wait();
        const a = actor();
        return damagesOf(a).filter((d) => d.reportedBy === a.id && !d.deletedAt).sort((p, q) => q.reportedAt.localeCompare(p.reportedAt)).map(toDamage);
      },

      async listMembers() {
        await wait();
        const a = requireRole('admin');
        return membershipsOf(a)
          .map(toMember)
          .filter((m): m is Member => m !== null)
          .sort((p, q) => p.displayName.localeCompare(q.displayName, 'de'));
      },

      async listEvents(filter) {
        await wait();
        const a = requireRole('admin');
        return state.events
          .filter((e) => e.tenantId === a.tenant.id)
          .filter((e) => (filter.entityType ? e.entityType === filter.entityType : true))
          .filter((e) => (filter.actorId ? e.actorId === filter.actorId : true))
          .filter((e) => (filter.action ? e.action === filter.action : true))
          .filter((e) => (filter.from ? e.createdAt.slice(0, 10) >= filter.from : true))
          .filter((e) => (filter.to ? e.createdAt.slice(0, 10) <= filter.to : true))
          .sort((p, q) => q.id - p.id)
          .map(toEvent);
      },

      async listTrash() {
        await wait();
        const a = requireRole('admin');
        const mine = damagesOf(a);
        const ids = new Set(mine.map((d) => d.id));
        const items: TrashItem[] = [];
        for (const d of mine.filter((x) => x.deletedAt)) {
          const ac = aircraftOf(a, d.aircraftId);
          items.push({ kind: 'damage', id: d.id, label: `${refOf(a, d.number)} ${d.title}`, context: ac?.registration ?? '', damageId: d.id, deletedAt: d.deletedAt ?? '', deletedBy: personRef(d.deletedBy), thumbUrl: null });
        }
        for (const i of state.images.filter((x) => x.deletedAt && ids.has(x.damageId))) {
          const d = mine.find((x) => x.id === i.damageId);
          items.push({ kind: 'damage_image', id: i.id, label: d ? `${refOf(a, d.number)} ${d.title}` : '', context: tx.admin.trash.kind.damage_image, damageId: i.damageId, deletedAt: i.deletedAt ?? '', deletedBy: personRef(i.deletedBy), thumbUrl: resolveUrl(i.thumbUrl) });
        }
        for (const c of state.comments.filter((x) => x.deletedAt && ids.has(x.damageId))) {
          const d = mine.find((x) => x.id === c.damageId);
          items.push({ kind: 'damage_comment', id: c.id, label: c.body.length > 80 ? `${c.body.slice(0, 77)}…` : c.body, context: d ? refOf(a, d.number) : '', damageId: c.damageId, deletedAt: c.deletedAt ?? '', deletedBy: personRef(c.deletedBy), thumbUrl: null });
        }
        // Deleted aircraft, restored through restoreAircraft like from the fleet screen.
        for (const ac of state.aircraft.filter((x) => x.tenantId === a.tenant.id && x.deletedAt)) {
          items.push({ kind: 'aircraft', id: ac.id, label: ac.registration, context: typeOf(ac.typeId)?.name ?? '', damageId: null, deletedAt: ac.deletedAt ?? '', deletedBy: personRef(ac.deletedBy), thumbUrl: null });
        }
        return items.sort((p, q) => q.deletedAt.localeCompare(p.deletedAt));
      },

      // ---------------------------------------------------------------- writes
      async createDamage(input, upload) {
        await wait();
        const a = actor();
        const isEditor = hasAtLeastRole(a.role, 'editor');
        const error = validateDamageTitle(input.title) ?? validateDescription(input.description);
        if (error) throw new DataError(400, error.message);
        const ac = aircraftOf(a, input.aircraftId);
        if (!ac || !ac.isActive) throw new DataError(404, tx.plan.notFound);
        // Like the API (0021): one or more known types.
        const categories = normalizeCategories(input.categories);
        if (!categories) throw new DataError(400, tx.create.categoryRequired);
        requireParticipation(a, 'report');
        // Like the API: the club's photo rule (media.ts).
        if (!mediaRequirementMet(a.role, settingsOf(a).mediaRequired, input.mediaCount ?? input.images.length)) {
          throw new DataError(400, de.media.required);
        }
        const status: DamageStatus = isEditor ? (input.status ?? 'bekannt') : 'gemeldet';
        // Like the API: these two need a reason, which only the review route takes.
        if (status === 'verworfen' || status === 'akzeptiert') throw new DataError(400, tx.errors.transition);
        const at = iso();
        const number = nextDamageNumber(a);
        const d: StoredDamage = {
          id: newId(),
          tenantId: a.tenant.id,
          number,
          aircraftId: ac.id,
          categories,
          view: input.view,
          x: input.x,
          y: input.y,
          title: input.title.trim(),
          description: input.description?.trim() || null,
          status,
          reportedBy: a.id,
          reportedAt: at,
          updatedAt: at,
          resolvedAt: status === 'behoben' ? at : null,
          reviewedBy: null,
          reviewedAt: null,
          reviewNote: null,
          mergedIntoId: null,
          deletedAt: null,
          deletedBy: null,
        };
        state.damages.push(d);
        const nearby = (await nearbyOf(a, d)).map((x) => x.id);
        const eventId = event(a, { entityType: 'damage', entityId: d.id, entityLabel: refOf(a, d.number), damageId: d.id, action: 'created', before: null, after: { ...damageSnapshot(d, numberOf), nearby }, reason: null });
        if (input.images.length) {
          const imgs = addStoredImages(d.id, input.images, a.id);
          event(a, { entityType: 'damage_image', entityId: imgs[0]?.id ?? d.id, entityLabel: refOf(a, d.number), damageId: d.id, action: 'image_added', before: null, after: { count: imgs.length, imageIds: imgs.map((i) => i.id) }, reason: null });
          upload?.onProgress?.({ done: imgs.length, total: imgs.length });
        }
        save();
        return { value: toDamage(d), eventId, imageFailures: [] };
      },

      async updateDamage(id, patch, opts) {
        await wait();
        const a = actor();
        const d = findDamage(a, id);
        // Like the API: changed since the form was opened → 409, the screen reloads.
        if (opts?.expectedUpdatedAt && Date.parse(opts.expectedUpdatedAt) !== Date.parse(d.updatedAt)) throw new DataError(409, tx.errors.stale, 'stale');
        const isEditor = hasAtLeastRole(a.role, 'editor');
        if (!isEditor) {
          if (d.reportedBy !== a.id) throw notFound();
          requireParticipation(a, 'report');
          const forbidden =
            (['status', 'view', 'x', 'y'] as const).some((k) => patch[k] !== undefined && patch[k] !== d[k]) ||
            (patch.categories !== undefined && JSON.stringify(normalizeCategories(patch.categories)) !== JSON.stringify(d.categories));
          if (forbidden || !isOwnUnreviewed(d, a)) throw new DataError(403, tx.errors.forbiddenField);
        }
        if (patch.categories !== undefined && !normalizeCategories(patch.categories)) throw new DataError(400, tx.create.categoryRequired);
        if (patch.title !== undefined) {
          const error = validateDamageTitle(patch.title);
          if (error) throw new DataError(400, error.message);
        }
        const error = validateDescription(patch.description);
        if (error) throw new DataError(400, error.message);
        if (patch.status !== undefined && patch.status !== d.status && (patch.status === 'verworfen' || patch.status === 'akzeptiert')) {
          throw new DataError(400, tx.errors.transition);
        }
        const before = damageSnapshot(d, numberOf);
        const statusBefore = d.status;
        if (patch.title !== undefined) d.title = patch.title.trim();
        if (patch.description !== undefined) d.description = patch.description?.trim() || null;
        if (patch.categories !== undefined) d.categories = normalizeCategories(patch.categories)!;
        if (patch.view !== undefined) d.view = patch.view;
        if (patch.x !== undefined) d.x = patch.x;
        if (patch.y !== undefined) d.y = patch.y;
        if (patch.status !== undefined) d.status = patch.status;
        if (d.status === 'behoben' && statusBefore !== 'behoben') d.resolvedAt = iso();
        if (d.status !== 'behoben') d.resolvedAt = null;
        d.updatedAt = iso();
        const after = damageSnapshot(d, numberOf);
        const action = statusBefore !== d.status ? 'status_changed' : 'updated';
        const eventId = event(a, { entityType: 'damage', entityId: d.id, entityLabel: refOf(a, d.number), damageId: d.id, action, before, after, reason: null }, opts);
        save();
        return { value: toDamage(d), eventId };
      },

      async reviewDamage(id, input) {
        await wait();
        const a = requireRole('editor');
        const d = findDamage(a, id);
        // 400 like the API (docs/api.md): the request is invalid, nothing changed in between.
        if (!canTransition(d.status, input.action)) throw new DataError(400, tx.errors.transition);
        const note = input.note?.trim() ?? '';
        if (actionNeedsReason(input.action) && note.length < REVIEW_NOTE_MIN_LENGTH) throw new DataError(400, de.review.reasonTooShort);
        const before = { ...damageSnapshot(d, numberOf), reviewedBy: d.reviewedBy, reviewedAt: d.reviewedAt, resolvedAt: d.resolvedAt };
        let copiedImageIds: string[] = [];
        if (input.action === 'merge') {
          const target = input.mergeIntoId ? findDamage(a, input.mergeIntoId) : null;
          if (!target || target.id === d.id || target.aircraftId !== d.aircraftId || target.status === 'verworfen') {
            throw new DataError(400, tx.errors.transition);
          }
          const copies = liveImages(d.id).map((i) => ({ ...i, id: newId(), damageId: target.id, createdAt: iso() }));
          state.images.push(...copies);
          copiedImageIds = copies.map((c) => c.id);
          d.mergedIntoId = target.id;
          target.updatedAt = iso();
          event(a, { entityType: 'damage', entityId: target.id, entityLabel: refOf(a, target.number), damageId: target.id, action: 'merged_report', before: null, after: { images: copies.length, from: d.number, description: d.description, copiedImageIds }, reason: note });
        }
        const next = targetStatus(input.action);
        if (d.status === 'gemeldet') {
          d.reviewedBy = a.id;
          d.reviewedAt = iso();
        }
        d.status = next;
        d.reviewNote = note || d.reviewNote;
        d.resolvedAt = next === 'behoben' ? iso() : null;
        d.updatedAt = iso();
        const eventId = event(a, {
          entityType: 'damage',
          entityId: d.id,
          entityLabel: refOf(a, d.number),
          damageId: d.id,
          action: 'status_changed',
          before,
          after: { ...damageSnapshot(d, numberOf), copiedImageIds, reviewAction: input.action },
          reason: note || null,
        });
        save();
        return { value: toDamage(d), eventId };
      },

      async revertReview(id, eventId) {
        await wait();
        const a = actor();
        const d = findDamage(a, id);
        const original = state.events.find((e) => e.id === eventId && e.tenantId === a.tenant.id && e.entityId === id && e.action === 'status_changed');
        if (!original || !original.before) throw new DataError(409, tx.toast.undoFailed);
        if (original.actorId !== a.id && !hasAtLeastRole(a.role, 'editor')) throw new DataError(403, tx.errors.notAllowed);
        const b = original.before;
        const before = damageSnapshot(d, numberOf);
        d.status = b['status'] as DamageStatus;
        d.reviewNote = (b['reviewNote'] as string | null) ?? null;
        d.mergedIntoId = (b['mergedIntoId'] as string | null) ?? null;
        d.reviewedBy = (b['reviewedBy'] as string | null) ?? null;
        d.reviewedAt = (b['reviewedAt'] as string | null) ?? null;
        d.resolvedAt = (b['resolvedAt'] as string | null) ?? null;
        d.updatedAt = iso();
        const copied = Array.isArray(original.after?.['copiedImageIds']) ? (original.after['copiedImageIds'] as string[]) : [];
        for (const img of state.images.filter((i) => copied.includes(i.id))) {
          img.deletedAt = iso();
          img.deletedBy = a.id;
        }
        const newId = event(a, { entityType: 'damage', entityId: d.id, entityLabel: refOf(a, d.number), damageId: d.id, action: 'status_changed', before, after: damageSnapshot(d, numberOf), reason: null }, { undoOf: eventId });
        save();
        return { value: toDamage(d), eventId: newId };
      },

      async deleteDamage(id, opts) {
        await wait();
        const a = actor();
        const d = findDamage(a, id);
        const allowed = hasAtLeastRole(a.role, 'editor') || isOwnUnreviewed(d, a) || isOwnUndo(a, opts);
        if (!allowed && d.reportedBy !== a.id) throw new DataError(404, tx.errors.notAllowed);
        requireParticipation(a, 'report');
        if (!allowed) throw new DataError(403, tx.errors.notAllowed);
        d.deletedAt = iso();
        d.deletedBy = a.id;
        const eventId = event(a, { entityType: 'damage', entityId: d.id, entityLabel: refOf(a, d.number), damageId: d.id, action: 'deleted', before: null, after: null, reason: null }, opts);
        save();
        return { value: toDamage(d), eventId };
      },

      async restoreDamage(id, opts) {
        await wait();
        const a = actor();
        if (!(a.role === 'admin' || isOwnUndo(a, opts))) throw new DataError(403, tx.errors.notAllowed);
        const d = findDamage(a, id, true);
        requireParticipation(a, 'report');
        d.deletedAt = null;
        d.deletedBy = null;
        const eventId = event(a, { entityType: 'damage', entityId: d.id, entityLabel: refOf(a, d.number), damageId: d.id, action: 'restored', before: null, after: null, reason: null }, opts);
        save();
        return { value: toDamage(d), eventId };
      },

      async addImages(damageId, images, opts) {
        await wait();
        const a = actor();
        const d = findDamage(a, damageId);
        requireParticipation(a, 'report');
        // The reporter at any time, the Technik always (owner, 17.10.2026) — the API's rule.
        if (!mayAddMedia(a.role, a.id, { reportedBy: d.reportedBy, deleted: d.deletedAt !== null })) {
          throw new DataError(403, tx.errors.notAllowed);
        }
        const created = addStoredImages(d.id, images, a.id);
        const merged = opts?.mergedReport;
        const eventId = event(
          a,
          merged
            ? { entityType: 'damage', entityId: d.id, entityLabel: refOf(a, d.number), damageId: d.id, action: 'merged_report', before: null, after: { images: created.length, imageIds: created.map((i) => i.id) }, reason: merged.note }
            : { entityType: 'damage_image', entityId: created[0]?.id ?? d.id, entityLabel: refOf(a, d.number), damageId: d.id, action: 'image_added', before: null, after: { count: created.length, imageIds: created.map((i) => i.id) }, reason: null },
          opts,
        );
        d.updatedAt = iso();
        save();
        opts?.onProgress?.({ done: created.length, total: images.length });
        return { value: created.map(toImage), eventId, eventIds: created.map(() => eventId), failures: [] };
      },

      async removeImage(imageId, opts) {
        await wait();
        const a = actor();
        const img = findImage(a, imageId, true);
        const d = findDamage(a, img.damageId);
        const allowed = hasAtLeastRole(a.role, 'editor') || (isOwnUnreviewed(d, a) && img.uploadedBy === a.id) || isOwnUndo(a, opts);
        if (!allowed) throw new DataError(403, tx.errors.notAllowed);
        requireParticipation(a, 'report');
        img.deletedAt = iso();
        img.deletedBy = a.id;
        const eventId = event(a, { entityType: 'damage_image', entityId: img.id, entityLabel: refOf(a, d.number), damageId: d.id, action: 'image_removed', before: null, after: null, reason: null }, opts);
        save();
        return { value: toImage(img), eventId };
      },

      async restoreImage(imageId, opts) {
        await wait();
        const a = actor();
        if (!(a.role === 'admin' || isOwnUndo(a, opts))) throw new DataError(403, tx.errors.notAllowed);
        const img = findImage(a, imageId, false);
        requireParticipation(a, 'report');
        img.deletedAt = null;
        img.deletedBy = null;
        const eventId = event(a, { entityType: 'damage_image', entityId: img.id, entityLabel: refOf(a, numberOf(img.damageId)), damageId: img.damageId, action: 'restored', before: null, after: null, reason: null }, opts);
        save();
        return { value: toImage(img), eventId };
      },

      // --- notices of the technicians (0022): the API's rules and texts, word for word.
      async listNotices() {
        await wait();
        const a = actor();
        const open = state.notices.filter((n) => n.tenantId === a.tenant.id && n.resolvedAt === null && aircraftOf(a, n.aircraftId));
        return activeNotices(open, berlinDay(now())).map((n) => toNotice(a, n));
      },

      async listNoticeHistory(aircraftId) {
        await wait();
        const a = requireRole('editor');
        if (!aircraftOf(a, aircraftId)) throw notFound();
        return state.notices
          .filter((n) => n.tenantId === a.tenant.id && n.aircraftId === aircraftId)
          .sort((x, y) => y.createdAt.localeCompare(x.createdAt))
          .map((n) => toNotice(a, n));
      },

      async createNotice(input) {
        await wait();
        const a = requireRole('editor');
        const today = berlinDay(now());
        const level = noticeLevel(input.level);
        const title = noticeTitle(input.title);
        const body = noticeBody(input.body);
        const validUntil = noticeDay(input.validUntil, today);
        const ac = aircraftOf(a, input.aircraftId);
        if (!ac) throw notFound();
        if (!noticeLevelAllowed(level, settingsOf(a).aircraftGrounding)) throw new DataError(403, de.notices.groundingOff);
        const at = iso();
        const n: StoredNotice = { id: newId(), tenantId: a.tenant.id, aircraftId: ac.id, level, title, body, validUntil, createdAt: at, createdBy: a.id, revisedAt: at, updatedAt: at, resolvedAt: null, resolvedBy: null };
        state.notices.push(n);
        const eventId = event(a, { entityType: 'aircraft_notice', entityId: n.id, entityLabel: `${ac.registration} · ${title}`, damageId: null, action: 'created', before: null, after: { registration: ac.registration, level, title, body, validUntil }, reason: null });
        save();
        return { value: toNotice(a, n), eventId };
      },

      async updateNotice(id, patch, opts) {
        await wait();
        const a = requireRole('editor');
        const n = findNotice(a, id);
        if (n.resolvedAt) throw new DataError(409, de.notices.alreadyResolved);
        if (opts?.expectedUpdatedAt && Date.parse(opts.expectedUpdatedAt) !== Date.parse(n.updatedAt)) throw new DataError(409, tx.errors.stale, 'stale');
        const today = berlinDay(now());
        const change: Partial<Pick<StoredNotice, 'level' | 'title' | 'body' | 'validUntil'>> = {};
        if (patch.level !== undefined) change.level = noticeLevel(patch.level);
        if (patch.title !== undefined) change.title = noticeTitle(patch.title);
        if (patch.body !== undefined) change.body = noticeBody(patch.body);
        if (patch.validUntil !== undefined) change.validUntil = noticeDay(patch.validUntil, today);
        if (Object.keys(change).length === 0) throw new DataError(400, de.fleet.noChange);
        // Raising to gesperrt needs the setting; keeping or lowering a grounding never does.
        if (change.level === 'gesperrt' && n.level !== 'gesperrt' && !noticeLevelAllowed('gesperrt', settingsOf(a).aircraftGrounding)) throw new DataError(403, de.notices.groundingOff);
        const before = { level: n.level, title: n.title, body: n.body, validUntil: n.validUntil };
        const revised = (change.level !== undefined && change.level !== n.level) || (change.title !== undefined && change.title !== n.title) || (change.body !== undefined && change.body !== n.body);
        const at = iso();
        Object.assign(n, change, { updatedAt: at }, revised ? { revisedAt: at } : {});
        const keys = (Object.keys(change) as (keyof typeof before)[]).filter((k) => before[k] !== n[k]);
        const registration = aircraftOf(a, n.aircraftId)?.registration ?? '';
        const eventId = event(a, { entityType: 'aircraft_notice', entityId: n.id, entityLabel: `${registration} · ${n.title}`, damageId: null, action: 'updated', before: Object.fromEntries(keys.map((k) => [k, before[k]])), after: Object.fromEntries(keys.map((k) => [k, n[k]])), reason: null });
        save();
        return { value: toNotice(a, n), eventId };
      },

      async resolveNotice(id) {
        await wait();
        const a = requireRole('editor');
        const n = findNotice(a, id);
        if (n.resolvedAt) throw new DataError(409, de.notices.alreadyResolved);
        const at = iso();
        Object.assign(n, { resolvedAt: at, resolvedBy: a.id, updatedAt: at });
        const registration = aircraftOf(a, n.aircraftId)?.registration ?? '';
        const eventId = event(a, { entityType: 'aircraft_notice', entityId: n.id, entityLabel: `${registration} · ${n.title}`, damageId: null, action: 'status_changed', before: { resolved: false, level: n.level }, after: { resolved: true, level: n.level }, reason: null });
        save();
        return { value: toNotice(a, n), eventId };
      },

      async acknowledgeNotice(id) {
        await wait();
        const a = actor();
        const n = state.notices.find((x) => x.id === id && x.tenantId === a.tenant.id);
        // Only what is shown can be read: a resolved or expired notice is gone for everyone.
        if (!n || !aircraftOf(a, n.aircraftId) || !isNoticeActive(n, berlinDay(now()))) throw notFound();
        const at = iso();
        const existing = state.noticeAcks.find((k) => k.noticeId === id && k.memberId === a.id);
        if (existing) existing.acknowledgedAt = at;
        else state.noticeAcks.push({ noticeId: id, memberId: a.id, acknowledgedAt: at });
        save();
        return toNotice(a, n);
      },

      async addComment(damageId, body) {
        await wait();
        const a = actor();
        const d = findDamage(a, damageId);
        requireParticipation(a, 'comment');
        const text = body.trim();
        if (!text || text.length > COMMENT_MAX) throw new DataError(400, tx.errors.notAllowed);
        const c = { id: newId(), damageId: d.id, authorId: a.id, body: text, createdAt: iso(), deletedAt: null, deletedBy: null };
        state.comments.push(c);
        const eventId = event(a, { entityType: 'damage_comment', entityId: c.id, entityLabel: refOf(a, d.number), damageId: d.id, action: 'created', before: null, after: { body: text }, reason: null });
        save();
        return { value: toComment(c), eventId };
      },

      async deleteComment(commentId, opts) {
        await wait();
        const a = actor();
        const c = findComment(a, commentId, true);
        if (!(c.authorId === a.id || hasAtLeastRole(a.role, 'editor'))) throw new DataError(403, tx.errors.notAllowed);
        requireParticipation(a, 'comment');
        c.deletedAt = iso();
        c.deletedBy = a.id;
        const eventId = event(a, { entityType: 'damage_comment', entityId: c.id, entityLabel: refOf(a, numberOf(c.damageId)), damageId: c.damageId, action: 'deleted', before: { body: c.body }, after: null, reason: null }, opts);
        save();
        return { value: toComment(c), eventId };
      },

      async restoreComment(commentId, opts) {
        await wait();
        const a = actor();
        if (!(a.role === 'admin' || isOwnUndo(a, opts))) throw new DataError(403, tx.errors.notAllowed);
        const c = findComment(a, commentId, false);
        requireParticipation(a, 'comment');
        c.deletedAt = null;
        c.deletedBy = null;
        const eventId = event(a, { entityType: 'damage_comment', entityId: c.id, entityLabel: refOf(a, numberOf(c.damageId)), damageId: c.damageId, action: 'restored', before: null, after: { body: c.body }, reason: null }, opts);
        save();
        return { value: toComment(c), eventId };
      },

      async addMember(input) {
        await wait();
        const a = requireRole('admin');
        const email = normalizeEmail(input.email);
        if (!isValidEmail(email)) throw new DataError(400, tx.login.invalidEmail);
        const existing = personToJoin(email, a.tenant.id);
        if (existing === 'unproven' || (existing && membershipsOf(a).some((m) => m.memberId === existing.id))) {
          throw new DataError(409, 'Diese Adresse ist schon eingetragen.');
        }
        const p = existing ?? personFor(email, input.displayName);
        const role: TenantRole = input.role === 'inactive' ? 'member' : input.role;
        const active = input.role !== 'inactive';
        // The demo sends nothing (no network): an active member counts as invited, like the API's delivered mail.
        const m = { tenantId: a.tenant.id, memberId: p.id, role, isActive: active, createdAt: iso(), invitation: active ? { state: 'sent' as const, at: iso() } : null };
        state.memberships.push(m);
        const eventId = event(a, { entityType: 'member', entityId: p.id, entityLabel: p.displayName, damageId: null, action: 'created', before: null, after: { email: p.email, displayName: p.displayName, role }, reason: null });
        save();
        return { value: toMember(m) as Member, eventId };
      },

      async updateMember(id, patch, opts) {
        await wait();
        const a = requireRole('admin');
        const m = membershipsOf(a).find((x) => x.memberId === id);
        const p = state.people.find((x) => x.id === id);
        if (!m || !p) throw notFound();
        const before = { role: m.role, isActive: m.isActive, displayName: p.displayName };
        const nextRole: TenantRole = patch.role === undefined || patch.role === 'inactive' ? m.role : patch.role;
        const nextActive = patch.role === 'inactive' ? false : (patch.isActive ?? m.isActive);
        const remainingAdmins = membershipsOf(a).filter((x) => x.memberId !== id && x.role === 'admin' && x.isActive).length;
        if (m.role === 'admin' && m.isActive && (nextRole !== 'admin' || !nextActive) && remainingAdmins === 0) {
          throw new DataError(409, 'Der letzte Administrator kann nicht entfernt werden.');
        }
        // The API's rules for a person of another club (security review 15.10.2026).
        const inOtherClub = state.memberships.some((x) => x.memberId === id && x.tenantId !== a.tenant.id);
        if (patch.displayName !== undefined && patch.displayName.trim() !== p.displayName && inOtherClub) {
          throw new DataError(409, de.foreignPerson.rename);
        }
        m.role = nextRole;
        m.isActive = nextActive;
        if (patch.displayName !== undefined) p.displayName = patch.displayName.trim();
        const eventId = event(a, { entityType: 'member', entityId: p.id, entityLabel: p.displayName, damageId: null, action: 'updated', before, after: { role: m.role, isActive: m.isActive, displayName: p.displayName }, reason: null }, opts);
        save();
        return { value: toMember(m) as Member, eventId };
      },

      async importMembers(rows, deactivateMissing, deactivate = [], invite = false) {
        await wait();
        const a = requireRole('admin');
        // The same checks as the API's parseImportBody: one address once, in rows or in deactivate.
        const listed = [...rows.map((r) => normalizeEmail(r.email)), ...deactivate.map((e) => normalizeEmail(e))];
        if (new Set(listed).size !== listed.length) throw new DataError(400, 'Adresse doppelt.');
        let added = 0;
        let updated = 0;
        let deactivated = 0;
        const inFile = new Set(listed);
        const byStatus = new Set(deactivate.map((e) => normalizeEmail(e)));
        // A club-added further address of somebody outside this club: the whole
        // import is refused before anything is written, as the API's transaction rolls back.
        rows.forEach((row, index) => {
          if (personToJoin(normalizeEmail(row.email), a.tenant.id) === 'unproven') {
            throw new DataError(409, `Zeile ${index + 1}: Diese Adresse ist schon eingetragen.`);
          }
        });
        const seen = new Set<string>();
        for (const row of rows) {
          const p = personFor(row.email, row.name);
          // Listed twice, under the first and a further address (0023): the first row counts, as in planImport.
          if (seen.has(p.id)) continue;
          seen.add(p.id);
          const m = membershipsOf(a).find((x) => x.memberId === p.id);
          // No role in the file: a new person is a member, an existing one keeps the role.
          const role: TenantRole = row.role === undefined ? (m?.role ?? 'member') : row.role === 'inactive' ? 'member' : row.role;
          if (!m) {
            // The server sends these in steps; the demo has no mail and shows them as waiting.
            state.memberships.push({ tenantId: a.tenant.id, memberId: p.id, role, isActive: true, createdAt: iso(), invitation: invite ? { state: 'pending', at: iso() } : null });
            added++;
          } else if (p.displayName !== row.name || m.role !== role || !m.isActive) {
            p.displayName = row.name;
            m.role = role;
            m.isActive = true;
            updated++;
          }
        }
        for (const m of membershipsOf(a)) {
          const p = state.people.find((x) => x.id === m.memberId);
          if (!p || !m.isActive || m.memberId === a.id) continue;
          const addresses = addressesOf(p);
          if (addresses.some((e) => byStatus.has(e)) || (deactivateMissing && !addresses.some((e) => inFile.has(e)))) {
            m.isActive = false;
            deactivated++;
          }
        }
        const summary = { added, updated, deactivated };
        const eventId = event(a, { entityType: 'member', entityId: '00000000-0000-0000-0000-000000000000', entityLabel: 'CSV-Import', damageId: null, action: 'import', before: null, after: summary, reason: null });
        save();
        return { value: { ...summary, invitations: invite ? added : 0 }, eventId };
      },

      async inviteMember(id) {
        await wait();
        const a = requireRole('admin');
        const m = membershipsOf(a).find((x) => x.memberId === id);
        if (!m) throw notFound();
        if (!m.isActive) throw new DataError(409, 'Nur aktive Mitglieder können eingeladen werden.');
        m.invitation = { state: 'pending', at: iso() };
        save();
        return toMember(m) as Member;
      },

      async endMemberSessions(id) {
        await wait();
        const a = requireRole('admin');
        if (!membershipsOf(a).some((m) => m.memberId === id)) throw notFound();
        if (state.memberships.some((x) => x.memberId === id && x.tenantId !== a.tenant.id && x.isActive)) {
          throw new DataError(409, de.foreignPerson.endSessions);
        }
      },

      // Same route as updateAircraft on the server ({ isActive }), same rules here.
      setAircraftActive: (id, active, opts) => source.updateAircraft(id, { isActive: active }, opts),

      async updateSettings(patch, opts) {
        await wait();
        const a = requireRole('admin');
        // Like PUT manage/settings: an unknown level is a 400, never stored.
        if (patch.memberParticipation !== undefined && !isMemberParticipation(patch.memberParticipation)) {
          throw new DataError(400, de.participation.invalid);
        }
        if (patch.mediaRequired !== undefined && typeof patch.mediaRequired !== 'boolean') {
          throw new DataError(400, 'Die Foto-Pflicht ist an oder aus.');
        }
        if (patch.aircraftGrounding !== undefined && typeof patch.aircraftGrounding !== 'boolean') {
          throw new DataError(400, 'Das Sperren von Flugzeugen ist an oder aus.');
        }
        const before = settingsOf(a);
        const next = { ...before, ...patch };
        state.settings[a.tenant.id] = next;
        const changed = Object.keys(patch) as (keyof Settings)[];
        const eventId = event(
          a,
          {
            entityType: 'setting',
            entityId: changed.join(','),
            entityLabel: changed.join(', '),
            damageId: null,
            action: 'updated',
            before: Object.fromEntries(changed.map((k) => [k, before[k]])),
            after: Object.fromEntries(changed.map((k) => [k, next[k]])),
            reason: null,
          },
          opts,
        );
        save();
        return { value: { ...next }, eventId };
      },

      async getClub() {
        await wait();
        return toTenant(requireRole('admin').tenant);
      },

      async getClubContact() {
        await wait();
        const t = actor().tenant;
        return { name: t.name, ...Object.fromEntries(TENANT_CONTACT_FIELDS.map((k) => [k, t[k]])) } as ClubContact;
      },

      async updateClub(patch, opts) {
        await wait();
        const a = requireRole('admin');
        // Name, notes and status belong to the platform; a club admin sends only contact data.
        const forbidden = ['name', 'slug', 'code', 'notes', 'isActive'].some((k) => k in patch);
        if (forbidden) throw new DataError(403, tx.errors.forbiddenField);
        const { before, after } = applyContact(a.tenant, patch);
        const eventId = tenantEvent(a.tenant, a.id, 'updated', before, after, opts);
        save();
        return { value: toTenant(a.tenant), eventId };
      },

      // Club logo (0019): the server's rule, word for word, then kept as a data URL on the device.
      async setClubLogo(pngBase64) {
        await wait();
        const a = requireRole('admin');
        const bytes = base64ToBytes(pngBase64);
        const verdict = bytes ? inspectLogo(bytes) : ({ ok: false, reason: 'not_png' } as const);
        if (!verdict.ok) throw new DataError(400, de.logo.refused[verdict.reason]);
        const had = Boolean(a.tenant.logo);
        a.tenant.logo = `data:image/png;base64,${pngBase64}`;
        const eventId = tenantEvent(a.tenant, a.id, 'image_added', { logo: had }, { logo: true });
        save();
        return { value: { logoUrl: a.tenant.logo }, eventId };
      },

      async removeClubLogo() {
        await wait();
        const a = requireRole('admin');
        const had = Boolean(a.tenant.logo);
        a.tenant.logo = null;
        const eventId = tenantEvent(a.tenant, a.id, 'image_removed', { logo: had }, { logo: false });
        save();
        return { value: { logoUrl: null }, eventId };
      },

      async changeMemberEmail(id, input) {
        await wait();
        const a = requireRole('admin');
        const m = membershipsOf(a).find((x) => x.memberId === id);
        const p = state.people.find((x) => x.id === id);
        if (!m || !p) throw notFound();
        const email = normalizeEmail(input);
        if (!isValidEmail(email)) throw new DataError(400, tx.login.invalidEmail);
        // The rules of decideEmailChange (packages/api/src/members/emailChange.ts), in its order; the demo has no platform manager.
        if (isAnonymizedLabel(p.email)) throw new DataError(409, de.emailChange.anonymized);
        if (state.memberships.some((x) => x.memberId === id && x.tenantId !== a.tenant.id)) throw new DataError(409, de.emailChange.other_membership);
        if (normalizeEmail(p.email) === email) throw new DataError(409, de.emailChange.unchanged);
        const holder = personByAnyAddress(email);
        if (holder?.id === id) throw new DataError(409, de.emailChange.own_further);
        if (holder) throw new DataError(409, de.emailChange.taken);
        p.email = email;
        // Like the API: the old sign-in link is gone; the next sign-in with the new address makes one.
        p.providers = [];
        // Like the API's event: neither the old nor the new address.
        const eventId = event(a, { entityType: 'member', entityId: id, entityLabel: p.displayName, damageId: null, action: 'updated', before: { emailChanged: false }, after: { emailChanged: true }, reason: null });
        save();
        return { value: toMember(m) as Member, eventId };
      },

      // Further addresses (0023): the rules of decideAdminAdd/-Remove
      // (packages/api/src/members/additionalEmail.ts), in their order; the demo has no platform manager.
      async addMemberEmail(id, input) {
        await wait();
        const a = requireRole('admin');
        const m = membershipsOf(a).find((x) => x.memberId === id);
        const p = state.people.find((x) => x.id === id);
        if (!m || !p) throw notFound();
        const email = normalizeEmail(input);
        if (!isValidEmail(email)) throw new DataError(400, tx.login.invalidEmail);
        if (isAnonymizedLabel(p.email)) throw new DataError(409, de.additionalEmail.anonymized);
        if (state.memberships.some((x) => x.memberId === id && x.tenantId !== a.tenant.id)) throw new DataError(409, de.additionalEmail.other_membership);
        if (addressesOf(p).includes(email)) throw new DataError(409, de.additionalEmail.own);
        if (personByAnyAddress(email)) throw new DataError(409, de.additionalEmail.taken);
        if ((p.additionalEmails ?? []).length >= MAX_ADDITIONAL_EMAILS) throw new DataError(409, de.additionalEmail.limit);
        p.additionalEmails = [...(p.additionalEmails ?? []), { id: newId(), email, source: 'club', createdAt: iso() }];
        // Like the API's event: no address.
        const eventId = event(a, { entityType: 'member', entityId: id, entityLabel: p.displayName, damageId: null, action: 'updated', before: { addressAdded: false }, after: { addressAdded: true }, reason: null });
        save();
        return { value: toMember(m) as Member, eventId };
      },

      async removeMemberEmail(id, emailId) {
        await wait();
        const a = requireRole('admin');
        const m = membershipsOf(a).find((x) => x.memberId === id);
        const p = state.people.find((x) => x.id === id);
        const address = p?.additionalEmails?.find((e) => e.id === emailId);
        if (!m || !p || !address) throw notFound();
        if (state.memberships.some((x) => x.memberId === id && x.tenantId !== a.tenant.id)) throw new DataError(409, de.additionalEmail.other_membership);
        p.additionalEmails = (p.additionalEmails ?? []).filter((e) => e.id !== emailId);
        const eventId = event(a, { entityType: 'member', entityId: id, entityLabel: p.displayName, damageId: null, action: 'updated', before: { addressRemoved: false }, after: { addressRemoved: true }, reason: null });
        save();
        return { value: toMember(m) as Member, eventId };
      },

      async anonymizeMember(id) {
        await wait();
        const a = requireRole('admin');
        const m = membershipsOf(a).find((x) => x.memberId === id);
        const p = state.people.find((x) => x.id === id);
        if (!m || !p) throw notFound();
        // The rules of decideAnonymize (packages/api/src/members/anonymize.ts), in its order.
        if (isAnonymizedLabel(p.email)) throw new DataError(409, de.anonymize.already_anonymized);
        if (state.memberships.some((x) => x.memberId === id && x.tenantId !== a.tenant.id && x.isActive)) throw new DataError(409, de.anonymize.other_membership);
        const otherAdmins = membershipsOf(a).filter((x) => x.memberId !== id && x.role === 'admin' && x.isActive).length;
        if (m.role === 'admin' && m.isActive && otherAdmins === 0) throw new DataError(409, de.anonymize.last_admin);
        const label = demoAnonymizedLabel(id);
        const before = { isActive: m.isActive, anonymized: false };
        m.isActive = false;
        p.email = label;
        p.displayName = label;
        p.avatar = null;
        p.providers = [];
        // Further addresses go too (0023), and a pending one.
        p.additionalEmails = [];
        p.pendingEmail = null;
        // Like the API's event: neither the old nor the new name or address.
        const eventId = event(a, { entityType: 'member', entityId: id, entityLabel: label, damageId: null, action: 'updated', before, after: { isActive: false, anonymized: true }, reason: null });
        save();
        return { value: toMember(m) as Member, eventId };
      },

      // ---------------------------------------------------------------- fleet
      async listFleetAdmin() {
        await wait();
        const a = requireRole('admin');
        // Like the API: live ones by sort order, the deleted ones at the end.
        return state.aircraft
          .filter((x) => x.tenantId === a.tenant.id)
          .sort((p, q) => Number(p.deletedAt !== null) - Number(q.deletedAt !== null) || p.sortOrder - q.sortOrder || p.registration.localeCompare(q.registration))
          .map(toFleetAircraft);
      },

      async createAircraft(input) {
        await wait();
        const a = requireRole('admin');
        const registration = parseRegistration(input.registration);
        usableType(a, input.typeId);
        const sortOrder =
          input.sortOrder !== undefined ? checkSortOrder(input.sortOrder) : liveAircraftOf(a).reduce((max, x) => Math.max(max, x.sortOrder), 0) + SORT_ORDER_STEP;
        checkRegistrationFree(a, registration, null);
        const ac: StoredAircraft = { id: newId(), tenantId: a.tenant.id, registration, typeId: input.typeId, isActive: true, sortOrder, deletedAt: null, deletedBy: null };
        state.aircraft.push(ac);
        const eventId = event(a, { entityType: 'aircraft', entityId: ac.id, entityLabel: ac.registration, damageId: null, action: 'created', before: null, after: aircraftSnapshot(ac), reason: null });
        save();
        return { value: toFleetAircraft(ac), eventId };
      },

      async updateAircraft(id, patch: AircraftPatch, opts) {
        await wait();
        const a = requireRole('admin');
        const ac = findFleetAircraft(a, id);
        if (ac.deletedAt) throw notFound();
        checkUndo(a, opts);
        if (Object.values(patch).every((v) => v === undefined)) throw new DataError(400, de.fleet.noChange);
        const registration = patch.registration !== undefined ? parseRegistration(patch.registration) : ac.registration;
        if (registration !== ac.registration) {
          if (damageCountOf(ac) > 0) throw new DataError(409, de.fleet.registrationLocked);
          checkRegistrationFree(a, registration, ac.id);
        }
        let reason: string | null = null;
        if (patch.typeId !== undefined && patch.typeId !== ac.typeId) {
          const next = usableType(a, patch.typeId);
          const current = typeOf(ac.typeId);
          if (current) reason = checkDrift(drift(damagesPerView(a, { aircraftId: ac.id }), await geometryOf(current), await geometryOf(next)), opts?.force === true);
        }
        const sortOrder = patch.sortOrder !== undefined ? checkSortOrder(patch.sortOrder) : ac.sortOrder;
        const before = aircraftSnapshot(ac);
        ac.registration = registration;
        ac.typeId = patch.typeId ?? ac.typeId;
        ac.sortOrder = sortOrder;
        ac.isActive = patch.isActive ?? ac.isActive;
        const diff = changed(before, aircraftSnapshot(ac));
        const eventId = event(a, { entityType: 'aircraft', entityId: ac.id, entityLabel: ac.registration, damageId: null, action: 'updated', before: diff.before, after: diff.after, reason }, opts);
        save();
        return { value: toFleetAircraft(ac), eventId };
      },

      async deleteAircraft(id, opts) {
        await wait();
        const a = requireRole('admin');
        const ac = findFleetAircraft(a, id);
        if (ac.deletedAt) throw notFound();
        checkUndo(a, opts);
        // Damages keep the aircraft: take it out of service instead (CLAUDE.md rule 28).
        if (damageCountOf(ac) > 0) throw new DataError(409, de.fleet.hasDamages);
        ac.deletedAt = iso();
        ac.deletedBy = a.id;
        const eventId = event(a, { entityType: 'aircraft', entityId: ac.id, entityLabel: ac.registration, damageId: null, action: 'deleted', before: { deletedAt: null }, after: { deletedAt: ac.deletedAt }, reason: null }, opts);
        save();
        return { value: toFleetAircraft(ac), eventId };
      },

      async restoreAircraft(id, opts) {
        await wait();
        const a = requireRole('admin');
        const ac = findFleetAircraft(a, id);
        if (!ac.deletedAt) throw new DataError(409, de.fleet.notDeleted);
        checkUndo(a, opts);
        const before = { deletedAt: ac.deletedAt };
        ac.deletedAt = null;
        ac.deletedBy = null;
        const eventId = event(a, { entityType: 'aircraft', entityId: ac.id, entityLabel: ac.registration, damageId: null, action: 'restored', before, after: { deletedAt: null }, reason: null }, opts);
        save();
        return { value: toFleetAircraft(ac), eventId };
      },

      async listFleetTypes() {
        await wait();
        const a = requireRole('admin');
        // Own first, then the catalogue (each by name), like GET manage/aircraft-types.
        const list = visibleTypes(a).sort((p, q) => Number(p.tenantId === null) - Number(q.tenantId === null) || p.name.localeCompare(q.name, 'de'));
        return Promise.all(list.map((t) => toFleetType(a, t)));
      },

      async createAircraftType(input) {
        await wait();
        const a = requireRole('admin');
        const code = input.code.trim().toUpperCase();
        if (!isValidTypeCode(code)) throw new DataError(400, de.fleet.codeFormat);
        const name = checkText(input.name, TYPE_NAME_MIN, TYPE_NAME_MAX, 'Der Name');
        const credit = checkText(input.credit, TYPE_CREDIT_MIN, TYPE_CREDIT_MAX, 'Die Quellenangabe');
        const aircraftClass = checkAircraftClass(input.aircraftClass);
        // The web finds a type by its code: no repeat of a catalogue code or an own one.
        if (visibleTypes(a).some((t) => t.code.toUpperCase() === code)) throw new DataError(409, de.fleet.codeTaken);
        const t: StoredType = { id: newId(), tenantId: a.tenant.id, code, name, credit, aircraftClass, mirrorRules: { ...DEFAULT_MIRROR_RULES }, drawings: {}, createdAt: iso() };
        state.types.push(t);
        const eventId = event(a, { entityType: 'aircraft_type', entityId: t.id, entityLabel: t.code, damageId: null, action: 'created', before: null, after: { code, name, credit, aircraftClass }, reason: null });
        save();
        return { value: await toFleetType(a, t, false), eventId };
      },

      async updateAircraftType(id, patch, opts) {
        await wait();
        const a = requireRole('admin');
        const t = ownType(a, id);
        if ('code' in patch) throw new DataError(400, de.fleet.codeFixed);
        checkUndo(a, opts);
        if (patch.name === undefined && patch.credit === undefined && patch.aircraftClass === undefined) throw new DataError(400, de.fleet.noChange);
        const next = {
          name: patch.name !== undefined ? checkText(patch.name, TYPE_NAME_MIN, TYPE_NAME_MAX, 'Der Name') : t.name,
          credit: patch.credit !== undefined ? checkText(patch.credit, TYPE_CREDIT_MIN, TYPE_CREDIT_MAX, 'Die Quellenangabe') : t.credit,
          aircraftClass: patch.aircraftClass !== undefined ? checkAircraftClass(patch.aircraftClass) : (t.aircraftClass ?? null),
        };
        const diff = changed({ name: t.name, credit: t.credit, aircraftClass: t.aircraftClass ?? null }, next);
        t.name = next.name;
        t.credit = next.credit;
        t.aircraftClass = next.aircraftClass;
        const eventId = event(a, { entityType: 'aircraft_type', entityId: t.id, entityLabel: t.code, damageId: null, action: 'updated', before: diff.before, after: diff.after, reason: null }, opts);
        save();
        return { value: await toFleetType(a, t, false), eventId };
      },

      async uploadDrawing(typeId, view, file, opts) {
        await wait();
        const a = requireRole('admin');
        const t = ownType(a, typeId);
        const total = 3;
        opts?.onProgress?.({ done: 0, total });
        // Read locally, never fetched: the demo has no network.
        const bytes = new Uint8Array(await file.blob.arrayBuffer());
        opts?.onProgress?.({ done: 1, total });
        // The server's checks, by the bytes (core's IHDR reader); the content type is the one
        // the client always sends with the PUT.
        const check = validateDrawing({ size: bytes.length, contentType: 'image/png', bytes });
        if (!check.ok) throw new DataError(400, DRAWING_MESSAGES[check.problem], check.problem);
        let src: string | null = null;
        try {
          src = (await options.prepareDrawing?.(file.blob)) ?? null;
        } catch {
          src = null;
        }
        const upload: StoredDrawingUpload = {
          id: newId(),
          tenantId: a.tenant.id,
          typeId: t.id,
          view,
          src: src ?? dataUrl('image/png', bytes),
          width: check.width,
          height: check.height,
          alpha: check.alpha,
          createdAt: iso(),
        };
        state.drawingUploads.push(upload);
        opts?.onProgress?.({ done: 2, total });
        const r = await registerDrawing(a, t.id, view, upload.id, opts);
        opts?.onProgress?.({ done: total, total });
        return r;
      },

      async confirmDrawing(typeId, view, uploadId, opts) {
        await wait();
        return registerDrawing(requireRole('admin'), typeId, view, uploadId, opts);
      },

      // ---------------------------------------------------------------- export
      async createExport() {
        await wait();
        const a = requireRole('admin');
        // A club admin's export leaves out the platform's own note on the club.
        return createExportFor(a.tenant, a.id);
      },

      async listExports() {
        await wait();
        return listExportsOf(requireRole('admin').tenant.id);
      },
    };
    return source;
  }

  // ------------------------------------------------------------------ news
  let newsSeenInMemory: Record<string, string[]> = {};
  function readNewsSeen(): Record<string, string[]> {
    try {
      const raw = storage?.getItem(DEMO_NEWS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as unknown;
        if (parsed && typeof parsed === 'object') return parsed as Record<string, string[]>;
      }
    } catch {
      /* unreadable: nothing seen */
    }
    return { ...newsSeenInMemory };
  }

  // ------------------------------------------------------------------ backend
  const backend: DemoBackend = {
    kind: 'demo',
    get state() {
      return state;
    },

    async me(): Promise<Me> {
      await wait();
      const p = currentPerson();
      const memberships = state.memberships
        .filter((m) => m.memberId === p.id && m.isActive)
        .map((m) => ({ m, t: state.tenants.find((t) => t.id === m.tenantId) }))
        .filter((x): x is { m: (typeof x)['m']; t: StoredTenant } => x.t !== undefined && x.t.isActive)
        .map(({ m, t }) => ({ tenantId: t.id, name: t.name, slug: t.slug, code: t.code, role: m.role, logoUrl: t.logo ?? null }))
        .sort((a, b) => a.name.localeCompare(b.name, 'de'));
      return { memberId: p.id, email: p.email, displayName: p.displayName, isPlatformManager: false, avatarUrl: p.avatar ?? null, memberships };
    },

    forTenant(tenantId) {
      let s = sources.get(tenantId);
      if (!s) {
        s = createTenantSource(tenantId);
        sources.set(tenantId, s);
      }
      return s;
    },

    platform,


    // Profile picture (0014): the server's rule, word for word, then kept as a data URL on the device.
    async setAvatar(jpegBase64) {
      await wait();
      const p = currentPerson();
      const bytes = base64ToBytes(jpegBase64);
      const verdict = bytes ? inspectAvatar(bytes) : ({ ok: false, reason: 'not_jpeg' } as const);
      if (!verdict.ok) throw new DataError(400, de.avatar.refused[verdict.reason]);
      p.avatar = `data:image/jpeg;base64,${jpegBase64}`;
      save();
      return backend.me();
    },

    async removeAvatar() {
      await wait();
      currentPerson().avatar = null;
      save();
      return backend.me();
    },

    // Further addresses of the persona (0023). No mail leaves the demo: the code is DEMO_EMAIL_CODE.
    async myEmails() {
      await wait();
      return myEmailsOf(currentPerson());
    },

    async addMyEmail(input) {
      await wait();
      const p = currentPerson();
      const email = normalizeEmail(input);
      if (!isValidEmail(email)) throw new DataError(400, tx.login.invalidEmail);
      // decideSelfRequest: only what is the persona's own is said out loud.
      if (addressesOf(p).includes(email)) throw new DataError(409, de.additionalEmail.own);
      if ((p.additionalEmails ?? []).length >= MAX_ADDITIONAL_EMAILS) throw new DataError(409, de.additionalEmail.limit);
      const expiresAt = new Date(now().getTime() + LOGIN_CODE_TTL_MINUTES * 60_000).toISOString();
      p.pendingEmail = { email, expiresAt, blocked: personByAnyAddress(email) !== undefined };
      save();
      return myEmailsOf(p);
    },

    async confirmMyEmail(code) {
      await wait();
      const p = currentPerson();
      const pending = p.pendingEmail;
      if (!pending || Date.parse(pending.expiresAt) <= now().getTime()) throw new DataError(409, de.additionalEmail.noPending);
      if (pending.blocked || code.trim() !== DEMO_EMAIL_CODE) throw new DataError(400, 'Code ist ungültig oder abgelaufen.');
      p.pendingEmail = null;
      if (personByAnyAddress(pending.email)) throw new DataError(409, de.additionalEmail.taken);
      if ((p.additionalEmails ?? []).length >= MAX_ADDITIONAL_EMAILS) throw new DataError(409, de.additionalEmail.limit);
      p.additionalEmails = [...(p.additionalEmails ?? []), { id: newId(), email: pending.email, source: 'self', createdAt: iso() }];
      ownAddressEvent(p, 'addressAdded');
      save();
      return myEmailsOf(p);
    },

    async removeMyEmail(id) {
      await wait();
      const p = currentPerson();
      if (!(p.additionalEmails ?? []).some((e) => e.id === id)) throw notFound();
      p.additionalEmails = (p.additionalEmails ?? []).filter((e) => e.id !== id);
      ownAddressEvent(p, 'addressRemoved');
      save();
      return myEmailsOf(p);
    },

    async announcements() {
      await wait();
      return demoAnnouncements(readNewsSeen()[currentPerson().id] ?? []);
    },

    // The demo is the tour itself; its own tour starts by itself (tourPersonaOf).
    async welcomeDue() {
      return false;
    },

    async finishWelcome() {},

    async acknowledgeAnnouncements(ids) {
      await wait();
      const me = currentPerson().id;
      const seen = readNewsSeen();
      seen[me] = [...new Set([...(seen[me] ?? []), ...ids])];
      try {
        storage?.setItem(DEMO_NEWS_KEY, JSON.stringify(seen));
      } catch {
        /* full or blocked: the cards come again next time, which is harmless */
      }
      newsSeenInMemory = seen;
    },

    async reset() {
      state = createSeed(now());
      save();
      newsSeenInMemory = {};
      try {
        storage?.removeItem(DEMO_NEWS_KEY);
      } catch {
        /* nothing to clear */
      }
    },
  };
  return backend;
}
